// ============================================================
// useCasevoStore.ts
// Global state management — updated with authentication state
// ============================================================
import { clearTaskScopeCache } from '../utils/taskScopeCache'
import { create } from 'zustand'
import type { Appearance } from '../types/appearance.types'
import { syncAccountAppearance } from '../utils/accountAppearance'
import { Case, RawCase, ViewingPaneToggles, UserRole, Witness, TpiContact, ViewingPaneHeading, CaseSectionKey } from '../types/case.types'
import { Client, CreateClientPayload, UpdateClientPayload } from '../types/client.types'
import { CalendarEntry } from '../types/calendar.types'
import { DocumentSearchResult } from '../types/documentSearch.types'
import type { Task, CreateTaskPayload, UpdateTaskPayload, ListTasksFilters } from '../types/tasks.types'
import type { CaseDeadline } from '../types/deadlines.types'
import type { NotificationPreferences } from '../types/notifications.types'
import type { Bill } from '../types/billing.types'
import type { EffectivePermissions } from '../constants/permissions'
import type { FirmUser } from '../types/user.types'
import { listUsersApi } from '../api/casevoApi'
import { VIEWING_PANE_HEADINGS } from '../constants/viewingPane.constants'
import { setCaseBaseUpdatedAt, setClientBaseUpdatedAt, setWitnessBaseUpdatedAt, setDeadlineBaseUpdatedAt, setWitnessBaseValues, setDeadlineBaseValues } from '../hooks/saveBaseVersion'
import { caseOpenErrorMessage, fetchCases, listWitnessesApi, listTpiContactsApi, listClientsApi, getClientByIdApi, createClientApi, updateClientApi, deleteClientApi, updateMeApi, listFilesApi, fetchCalendarApi, fetchDocumentSearchApi, listTasksApi, createTaskApi, updateTaskApi, deleteTaskApi, listCaseDeadlinesShared, fetchCaseByIdShared, FileAttachment, getFirmColourSchemeApi, getUserColourSchemeApi, getCaseHistoryVisibilityApi, listFavouritesApi, addFavouriteApi, removeFavouriteApi, reorderFavouritesApi, isRestrictedClientError } from '../api/casevoApi'
import type { CaseHistoryVisibility, Favourite, FavouriteItemType } from '../api/casevoApi'
import { placeToSearch } from '../navigation/placeUrl'
import { claimOfRaw } from '../utils/caseClaim'
import { clearDocumentSearch } from '../utils/documentSearchMemory'
// Stage 1 of the view-history stack. One entry per view the user
// visited, captured by pushViewHistory (called from closeAllTakeovers
// BEFORE any flag is zeroed, so the snapshot reads the view being
// LEFT, not the one being opened). clientId / caseId are the entity
// identifiers a restore would need to reconstruct the same view
// (clientDetail needs a clientId to re-select; caseDetail needs a
// caseId to re-fetch). Kept optional so 'view' alone is meaningful
// for entityless views (dashboard, settings, admin, calendar,
// documentSearch, tasks, clientSearch, caseList).
export type ViewDescriptor = {
  view: 'dashboard' | 'caseList' | 'settings' | 'admin' | 'clientSearch'
      | 'clientDetail' | 'calendar' | 'documentSearch' | 'tasks' | 'caseDetail' | 'billing'
  clientId?: string
  caseId?: string
  // Stage 4c: captured for the 'caseList' descriptor only. Absent
  // on older trail entries (pre-4c) and on every other view; the
  // restoreView caseList branch treats undefined as "don't touch
  // the current tab" so upgrade is seamless.
  listTab?: 'all' | 'recent'
  // Case-redesign Stage 1: captured for a 'caseList' descriptor when the
  // full case was opened FROM the quick-view modal. On restore (Back), the
  // caseList branch sets pendingQuickViewCaseId so CaseListScreen re-opens
  // the modal for this case — so Back retraces one step (modal), not two
  // (list). Absent on every other caseList entry, so normal Back is unchanged.
  quickViewCaseId?: string
  // 'caseDetail' only: the case section that was showing, so Back/Forward
  // land on that exact section (restoreView seeds caseSectionMemo from it).
  caseSection?: CaseSectionKey
  // 'clientDetail' only: the client tab that was showing (clientSectionMemo),
  // so Back/Forward land on that tab (restoreView seeds the memo from it).
  clientSection?: string
  // The mounted screen's own sub-place (an open bill, a section, a filter) —
  // see navigation/navigate.ts usePlaceParam. Absent when the screen has none.
  params?: Record<string, string>
}
// Shape of the authenticated user
// True when any address part holds non-blank text (case hydration).
const hasAddressPart = (...parts: (string | null | undefined)[]): boolean =>
  parts.some((p) => typeof p === 'string' && p.trim() !== '')

export interface AuthUser {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  firmId: string
  // B3 — position printed when they sign a bill (admin-set).
  jobTitle?: string | null
  createdAt?: string  // ISO date returned by GET /api/auth/me
  viewingPaneToggles?: Partial<ViewingPaneToggles>
  // Email-notification preferences. Optional + Partial because
  // a brand-new account row returns an empty `{}` from the
  // backend and future-added prefs won't be on every user yet.
  // The NotificationsPanel in Settings merges this over
  // DEFAULT_NOTIFICATION_PREFERENCES at render time.
  notificationPreferences?: Partial<NotificationPreferences>
  // Avatar colour — a hex string the user picks from the
  // presets in Settings → Appearance. Optional + falls back
  // to the Avatar component's DEFAULT_AVATAR_COLOUR (#2d6a4f
  // / brand green) so existing accounts without a saved
  // choice still get a sensible default. Format is
  // intentionally just `string` (the backend column is
  // format-agnostic, ≤ 32 chars) — the frontend picker
  // restricts to the curated preset list at write time.
  avatarColour?: string
  // Avatar rendering style, paired with avatarColour. Backend column
  // defaults to 'solid' (the original look). Optional here because the
  // partial user object returned by login lacks it until getMe hydrates
  // the full profile — Avatar defaults to 'solid' when undefined.
  avatarStyle?: 'solid' | 'soft' | 'icon'
  // The theme choice saved on the account (Settings → Appearance), from
  // GET / PATCH /api/auth/me; null = never saved there. See utils/theme.ts.
  appearance?: Appearance | null
  // A ready-to-render presigned GET URL for the user's uploaded avatar
  // photo, derived server-side from avatarPhotoKey by getMe/updateMe
  // (fresh on each load). null / undefined = no photo → the styled
  // initial shows. The raw R2 key is never sent to the client.
  avatarPhotoUrl?: string | null
  // 2FA enforcement flags, both supplied by GET /api/auth/me. Optional
  // because the partial user object returned by login lacks them until
  // getMe hydrates the full profile.
  //   firmRequire2fa   — does this user's FIRM require all members to
  //                      have 2FA enabled?
  //   twoFactorEnabled — is THIS user's own 2FA fully enabled? True only
  //                      when confirmed — a mid-enrolment (un-confirmed)
  //                      setup yields false, so it doesn't satisfy
  //                      enforcement.
  // The app-load gate forces setup when firmRequire2fa && !twoFactorEnabled.
  firmRequire2fa?: boolean
  twoFactorEnabled?: boolean
  // The user's firm name (from getMe's firm join). Surfaced for the
  // case-summary PDF letterhead. Optional — older cached sessions won't
  // have it until the next getMe refresh.
  firmName?: string | null
  // B4c — the firm's e-signature pilot is on (GET /api/auth/me). Off / missing
  // → nothing about e-signature (signing, mobile numbers) shows.
  firmEsignPilot?: boolean
  // The user's email signature (HTML or plain text), edited in Settings and
  // auto-appended to a new compose body. Optional — '' / undefined = none.
  emailSignature?: string
  // Granular permissions (Stage 1): the current user's RESOLVED effective
  // permissions from getMe (admin=all; else per-user override, else firm
  // default). Read-only on the client — the backend is the authoritative gate.
  // Optional — older cached sessions won't have it until the next getMe.
  effectivePermissions?: EffectivePermissions
}
// Which unauthenticated screen the user is on. Replaces the local
// `showRegister` boolean that previously lived in App.tsx, so that
// the Forgot Password flow can navigate between five distinct screens
// (login / register / requestReset / acceptReset / confirmation)
// from anywhere — including the URL-token deep-link parser on mount.
// Settings left-nav sections (Option B redesign).
export type SettingsSection = 'account' | 'email' | 'appearance' | 'notifications' | 'firm'
export type AuthView = 'login' | 'register' | 'requestReset' | 'acceptReset' | 'confirmation' | 'acceptInvite'
// Which view opened the currently-viewing case. Set by every
// open-case trigger BEFORE setCurrentCaseById, read by Case
// Detail's Back handler to route the user back to the originating
// view. 'caseList' is the default fallback — falls through to
// CaseListScreen which is what ViewingPane renders when currentCase
// is null and no takeover is mounted.
export type CaseOpenedFrom = 'caseList' | 'clientSearch' | 'documentSearch' | 'calendar' | 'dashboard' | 'taskList' | 'quickView'
// Which view opened the currently-viewing task in TasksView's form
// mode. Set by every open-task trigger BEFORE setPendingTaskId, read
// by TasksView's Back handler to route back to the originating view.
// Two producers today: CalendarView and ClientSearchView (per-client
// Tasks section). null = no upstream view to return to (e.g. user
// clicked a task from inside TasksView's own list); Back just closes
// the form and stays on the list.
export type TaskOpenedFrom = 'calendar' | 'clientSearch' | 'dashboard' | null
interface CasevoStore {
  // ── AUTH STATE ──────────────────────────────────────────
  authToken: string | null
  authUser: AuthUser | null
  isAuthenticated: boolean
  setAuth: (token: string, user: AuthUser) => void
  setAuthUser: (user: AuthUser) => void
  // ★ Live identity update from the 'user-updated' socket event (a firm
  // member renamed themselves / changed avatar). Patches the central
  // firmUsers map (everything that renders people BY ID re-renders) and
  // authUser when it's us (e.g. the same person in another tab).
  applyUserDisplayUpdate: (u: { id: string; firstName: string; lastName: string; avatarColour?: string | null; avatarStyle?: string | null; avatarPhotoUrl?: string | null }) => void
  // Bumped whenever the signed-in user's role or effectivePermissions change
  // (e.g. an admin grants "See colleagues' tasks" — pushed live). Screens
  // that cache permission-scoped data (calendar Firm view, My Work, case
  // list) key on it to drop stale caches and refetch.
  permissionsVersion: number
  clearAuth: () => void
  // ── COLOUR SCHEMES (action-type colour cascade) ─────────
  // firm house colours + the user's live scheme, each { actionType:
  // token }. Loaded once on app bootstrap (loadColourSchemes). Empty
  // {} = no overrides → the render resolves shipped defaults (identical
  // to pre-cascade). Reset to {} on clearAuth.
  firmColourScheme: Record<string, string>
  userColourScheme: Record<string, string>
  loadColourSchemes: () => Promise<void>
  // Update the cached firm scheme (e.g. after an admin saves it in
  // Settings) so live timeline rows reflect the new colours immediately,
  // without a reload — the Stage 2 cascade reads this slice.
  setFirmColourScheme: (scheme: Record<string, string>) => void
  // Update the cached USER scheme (after the Settings "Timeline Colours"
  // panel saves/reverts/restores/resets) so the user's OWN timeline rows
  // recolour immediately — the firm scheme is left untouched.
  setUserColourScheme: (scheme: Record<string, string>) => void
  // ── CASE HISTORY VISIBILITY (firm setting) ──────────────
  // Who (besides admins) may view a case's History section:
  // 'admin' | 'assignee' | 'firm'. null = not loaded yet — callers
  // treat null as "hide History for non-admins" (fail-safe: better to
  // hide than flash a section that then 403s). Loaded member-readable on
  // the same isAuthenticated effect as the colour schemes; updated
  // in-place when an admin saves the setting. Reset to null on clearAuth.
  caseHistoryVisibility: CaseHistoryVisibility | null
  setCaseHistoryVisibility: (value: CaseHistoryVisibility) => void
  // ── SHARED FIRM USERS (with avatar fields) ──────────────
  // The firm's users, loaded ONCE from listUsersApi (admin-only) and
  // reused wherever a member lookup is needed (task/case assignee picker,
  // activity/history actor avatars). Replaces the per-component local
  // fetches. loadFirmUsers() is load-once (skips if already loaded unless
  // forced). It is GRACEFUL for non-admins: listUsers 403s for them, so
  // the slice simply stays empty — no error surfaced (their avatars come
  // from a backend join in a later step, not this slice). getFirmUserById
  // is the lookup helper. Reset on clearAuth.
  firmUsers: FirmUser[]
  firmUsersLoaded: boolean
  firmUsersLoading: boolean
  loadFirmUsers: (force?: boolean) => Promise<void>
  getFirmUserById: (id: string) => FirmUser | null
  // ── FAVOURITES (sidebar pins) ───────────────────────────
  // The user's pinned favourites (case / client / task). null = not
  // loaded yet; [] = loaded, none pinned. Loaded on the same
  // isAuthenticated effect as the colour schemes; kept in sync by
  // toggleFavourite so a star flips instantly. Reset to null on clearAuth.
  favourites: Favourite[] | null
  loadFavourites: () => Promise<void>
  // Cheap membership check for the star (filled vs outline).
  isFavourited: (itemType: FavouriteItemType, itemId: string) => boolean
  // Add (if not pinned) or remove (if pinned). Optimistic — the cache is
  // updated immediately so the star flips at once; on API failure the
  // optimistic change is reverted (see body).
  toggleFavourite: (payload: {
    itemType: FavouriteItemType
    itemId: string
    label: string
    subSection?: string | null
  }) => Promise<void>
  // Transient, user-facing message when a favourite op fails — notably
  // the 20-favourite cap ("Favourites limit reached…"). Surfaced by a
  // small toast (FavouritesErrorToast) and auto-cleared. null = no error.
  favouritesError: string | null
  setFavouritesError: (message: string | null) => void
  // Remove a favourite by its ROW id (no itemType/itemId toggle lookup) —
  // used by the sidebar's dead-favourite self-heal when a click reveals
  // the target is gone (e.g. a case favourite whose case was deleted).
  // Optimistically drops the row from the cache, optionally surfaces an
  // informational message via the shared favouritesError toast, then
  // deletes server-side BEST-EFFORT (a failure is logged, NOT reverted —
  // the target is gone, so keeping the row out is the correct end state).
  removeFavouriteById: (id: string, toastMessage?: string) => Promise<void>
  // Reorder the favourites to the given id order (drag-to-reorder in the
  // sidebar). Optimistic — reorders favourites[] at once, persists via
  // reorderFavouritesApi, reconciles with the returned normalised list;
  // reverts to the previous order + surfaces favouritesError on failure.
  reorderFavourites: (orderedIds: string[]) => Promise<void>
  // ── AUTH VIEW (unauthenticated routing) ────────────────
  // Drives which screen App.tsx renders when isAuthenticated is
  // false. pendingResetToken is set by the App.tsx mount-time URL
  // parser when a reset link drops the user on /?token=… — the
  // Accept Reset screen reads it when it renders.
  authView: AuthView
  pendingResetToken: string | null
  pendingInviteToken: string | null
  // "Open in new tab" deep-link: a case id parsed from ?caseId=… on load.
  // Stashed here and consumed once authenticated (setCurrentCaseById), so a
  // logged-out tab still opens the case after the user logs in. Mirrors the
  // pending-token pattern.
  pendingCaseId: string | null
  setPendingCaseId: (id: string | null) => void
  setAuthView: (view: AuthView) => void
  setPendingResetToken: (token: string | null) => void
  setPendingInviteToken: (token: string | null) => void
  // ── NAVIGATION STATE ───────────────────────────────────
  showSettings: boolean
  setShowSettings: (show: boolean) => void
  // showAdmin: takeover for the Admin page (admin-only firm
  // administration, v1 has the audit log viewer; user invites
  // / removal / firm dashboard land here later). Same mutual-
  // exclusion shape as the other takeover flags — sidebar
  // handlers clear siblings before flipping their own.
  showAdmin: boolean
  setShowAdmin: (show: boolean) => void
  // showClientSearch: Phase 2b sidebar — Client Search zone takes
  // over the main content area (mirrors showSettings). App.tsx
  // checks this BEFORE showSettings so Client Search opened from
  // the Settings screen wins; closing it falls through to whatever
  // else was true (Settings, ViewingPane, CaseListScreen).
  showClientSearch: boolean
  setShowClientSearch: (show: boolean) => void
  showCalendar: boolean
  setShowCalendar: (show: boolean) => void
  showDocumentSearch: boolean
  // Invoicing Stage 1 — the firm-wide Billing › Bills takeover (sidebar).
  showBilling: boolean
  openBilling: () => void
  setShowDocumentSearch: (show: boolean) => void
  // ── Tasks zone ────────────────────────────────────────────
  showTasks: boolean
  setShowTasks: (show: boolean) => void
  // Stage B: Dashboard is the app's landing view. Same boolean-flag
  // shape as every other takeover, but its initial value is `true` —
  // that's how "Dashboard is the default after login" is expressed
  // (no explicit "on login, set showDashboard = true" call exists;
  // the initial-value seed handles it, and clearAuth re-seeds `true`
  // on logout so re-login lands on Dashboard again). App.tsx puts
  // it at the TOP of the renderMain() cascade so it wins over every
  // other flag when true.
  showDashboard: boolean
  setShowDashboard: (show: boolean) => void
  // Stage B. Convenience action that sets EVERY takeover/view flag
  // to false in one place. Replaces the per-handler "close the other
  // N flags" pattern that grew brittle as flags accumulated (Sidebar
  // had 6 handlers each closing 5 siblings — one missed line per new
  // flag = a real bug). Nav handlers now do:
  //   closeAllTakeovers()
  //   setShowX(true)
  // Includes showDashboard so opening any OTHER takeover from the
  // Dashboard closes it correctly. The handler that opens the
  // Dashboard itself calls closeAllTakeovers() first, then flips
  // its own flag on — no self-close race.
  closeAllTakeovers: () => void
  // ── Navigation history (navigation/navigate.ts) ─────────────
  // Every browser history entry carries its place in history.state
  // ({ casevoSeq, place }), so Back/Forward restore straight from the entry
  // (also after a refresh). casevoSeq is the entry's depth in this app
  // session: the landing entry is 0, each push is +1. currentSeq = the
  // entry on screen; the popstate handler compares the target's seq with it
  // to tell Back from Forward, and seq 0 means "nothing to go Back to".
  currentSeq: number
  // The mounted screen's sub-place (usePlaceParam writes it; snapshotCurrentView
  // reads it). placeNonce bumps whenever a navigation / restore REPLACES it
  // from outside, so a still-mounted screen re-applies its params.
  placeParams: Record<string, string>
  placeNonce: number
  setPlaceParam: (key: string, value: string | null) => void
  // Pure read of current store state → descriptor for whatever
  // view is showing right now. Precedence mirrors App.tsx's
  // renderMain cascade.
  snapshotCurrentView: () => ViewDescriptor
  // Push a browser history entry: the CURRENT entry is first re-stamped
  // with the place being left (`leaving`, default = a snapshot now), then a
  // new entry is pushed. The place sync (navigate.ts) keeps the new entry's
  // place + URL in step as the destination renders. No-op mid-restore.
  pushViewHistory: (leaving?: ViewDescriptor) => void
  // ── Back-restore guard ─────
  // Guard flag: while a browser popstate is being processed
  // (restoreView), any incidental call into
  // pushViewHistory becomes a no-op. Prevents a feedback loop
  // where restoreView's inline flag writes could trigger a
  // downstream closeAllTakeovers → pushViewHistory (they don't
  // today, but belt-and-braces so the invariant holds even if
  // a caller changes later). App.tsx's popstate handler wraps
  // the pop+restore in a try/finally that flips this on and
  // back off.
  isRestoringView: boolean
  setIsRestoringView: (v: boolean) => void
  // ── Case-list tab (Stage 4c) ────────────────────────────
  // The CaseListScreen "All Cases" vs "Recent (last 24h)" toggle,
  // lifted out of the component's useState into the store so:
  //   1) The tab survives CaseListScreen's mount/unmount cycle
  //      when the user opens a case and hits Back (previously the
  //      local useState re-initialised to `true` on every remount,
  //      resetting the user to Recent even if they'd been on All).
  //   2) snapshotCurrentView captures it onto the viewHistory
  //      trail as { view: 'caseList', listTab: 'recent' | 'all' }
  //      so a Back-restore reproduces the exact tab the user was on.
  //   3) It persists across a full page reload via a mirrored
  //      localStorage write-through in setCaseListRecentOnly — same
  //      pattern as CaseListScreen's sortKey (casevo_case_list_sort).
  // NOT reset in clearAuth (see docblock there) so it matches the
  // sortKey lifecycle exactly.
  caseListRecentOnly: boolean
  // Accepts either a boolean value OR a functional updater
  // (prev) => next — same shape as React's SetStateAction<boolean>.
  // Widened so CaseListScreen's existing setter callsites work
  // verbatim after swapping useState for the store selector; the
  // toggle-button callsite passes `(v) => !v` (functional), the
  // empty-state Show-All button passes `false` (value).
  setCaseListRecentOnly: (v: boolean | ((prev: boolean) => boolean)) => void
  setCurrentSeq: (seq: number) => void
  // Apply a ViewDescriptor to the store — inline flag zeroing so
  // we do NOT go through closeAllTakeovers (which now pushes to
  // viewHistory and would defeat the point of Back). Takeover
  // flags are written in a single set() call; the target flag
  // flips on and everything else off. Does NOT resurrect one-shot
  // deep-link intents (caseInitialSection / taskIntentId /
  // pendingTaskId / pendingClientId) — those are either already
  // null (consumed by their owning view's mount effect) or
  // whatever they were, and restoring a view shouldn't fire a
  // deep-link a second time. Deliberately does NOT set
  // caseOpenedFrom — a browser-Back restore is not an "opened
  // from X" navigation; the in-app ← Back breadcrumb is separate
  // (owned by ViewingPane.goBack, Stage 4 scope).
  //
  // Stage 3: caseDetail and clientDetail now truly restore.
  //   - caseDetail: awaits setCurrentCaseById(caseId) to re-fetch
  //     the case row + witnesses. On rejection (deleted / 403 /
  //     404), falls back to caseList (currentCase null, flags
  //     zeroed) so the user sees the list, not a blank screen.
  //   - clientDetail: resolves the Client object via a three-tier
  //     lookup (current selectedClient → clients[] cache → fetch
  //     by id). Falls back to clientSearch mode on fetch failure.
  //
  // Returns Promise<void> — the async paths (case/client fetch)
  // must be awaited by the popstate handler so isRestoringView
  // stays true across the whole restore, not just the sync flag
  // writes. Sync paths (dashboard/tasks/etc.) simply return an
  // already-resolved promise.
  restoreView: (descriptor: ViewDescriptor) => Promise<void>
  // ── Cross-view navigation ─────────────────────────────────
  // Set by a view that wants another view to open with a
  // specific entity preselected. Consumed by the target view's
  // mount-time effect, then cleared.
  pendingTaskId: string | null
  setPendingTaskId: (id: string | null) => void
  pendingClientId: string | null
  setPendingClientId: (id: string | null) => void
  // Diary batch (?day cold-open): a 'YYYY-MM-DD' civil day the Calendar
  // should open ON next mount — set by App.tsx's ?day= deep-link (e.g. the
  // reminder email CTA). CalendarView's mount effect switches to Day view,
  // anchors on it, opens the day panel, then clears the slot. null = none.
  pendingCalendarDate: string | null
  setPendingCalendarDate: (day: string | null) => void
  // Diary batch: an appointment id the Calendar should OPEN (in its read-only
  // VIEW) on next mount / when set — from an appointment-invite bell notification.
  pendingCalendarAppointmentId: string | null
  setPendingCalendarAppointmentId: (id: string | null) => void
  // Which client section (activeClientSection key) the client view should
  // OPEN ON next time it resolves a client — set by a favourite click so a
  // client-section favourite restores its section. One-shot: consumed +
  // cleared by ClientSearchView's client-open effect. null = default
  // landing (notes/activity per the existing rule).
  pendingClientSection: string | null
  setPendingClientSection: (section: string | null) => void
  // One-shot: open the client Activity feed with its CASE filter pre-applied
  // (the case quick-view's "View all activity"). Consumed + cleared by
  // ClientSearchView's client open, only for the matching client.
  pendingClientActivityCase: { clientId: string; caseId: string } | null
  setPendingClientActivityCase: (v: { clientId: string; caseId: string } | null) => void
  // One-shot: open THIS bill once its case's Billing section mounts (a
  // bill-approval bell row / email link). Consumed + cleared by
  // CaseBillingSection, only for the matching case. `initial` = the bill when
  // the caller already loaded it (the bell does), so it paints at once.
  pendingBill: { billId: string; caseId: string; initial?: Bill } | null
  setPendingBill: (v: { billId: string; caseId: string; initial?: Bill } | null) => void
  // (Big-audit batch 4, item 10) "Billing" pressed in the case's section list while
  // already in that section: a tick the open bill listens for.
  billingListTick: number
  requestBillingList: () => void
  // One-shot: open the "Create bill" flow once THIS case's Billing section
  // has loaded (the Activity "Invoice" action — case page or client tab). Consumed by
  // CaseBillingSection for the matching case.
  pendingRaiseBillCaseId: string | null
  setPendingRaiseBillCaseId: (caseId: string | null) => void
  // The client tab the user was last on, per client. ClientSearchView
  // unmounts when a task / case takes over, so its local activeClientSection
  // is lost; on remount for the SAME client it restores this instead of the
  // default notes/activity landing (close task / Back from case → same tab).
  clientSectionMemo: { clientId: string; section: string } | null
  setClientSectionMemo: (memo: { clientId: string; section: string } | null) => void
  // One-shot: which Settings section to open on next Settings mount. Set
  // by the header avatar click (→ 'account', where the identity card +
  // "Change avatar" live); consumed + cleared by Settings' mount effect so
  // a normal Settings open uses the remembered section. Mirrors
  // pendingClientSection.
  pendingSettingsSection: SettingsSection | null
  setPendingSettingsSection: (section: SettingsSection | null) => void
  // One-shot: open Admin → Team with THIS member's modal (bell deep-link
  // from an email-change request). Consumed + cleared by TeamPanel.
  pendingAdminMemberId: string | null
  setPendingAdminMemberId: (id: string | null) => void
  // Deep-link routing intents (deadline/task feature — routeByItemType
  // shared helper). Set by the helper BEFORE nav; consumed and cleared
  // by the target view's mount effect on first read. Cleared-after-use
  // is load-bearing to prevent cross-case leak — e.g. if we set
  // caseInitialSection='deadlines' for case A, then the user opens
  // case B without the intent, case B must not inherit A's target.
  //
  // caseInitialSection: which case-detail section anchor to scroll to
  // on next case open. Consumed by ViewingPane's mount effect (plain
  // scrollIntoView — no auto-focus, so we don't drop the cursor into
  // a form the user didn't invoke). Typed as ViewingPaneHeading | null
  // for compile-time safety against typos in the anchor key.
  caseInitialSection: ViewingPaneHeading | null
  setCaseInitialSection: (section: ViewingPaneHeading | null) => void
  // activeCaseSection: which case-detail section the nav+panel is
  // currently showing (case-detail redesign). Store-backed (not ViewingPane
  // local state) so section components can ADVANCE it — the "Mark complete
  // and continue" button sets the next section here, swapping the panel.
  // null = nothing selected yet (pre-reconcile) / no case open.
  // 'overview' = the case-page landing (Stage 1 shell redesign).
  activeCaseSection: CaseSectionKey | null
  setActiveCaseSection: (section: CaseSectionKey | null) => void
  // The case section the user was last on, per case (mirror of
  // clientSectionMemo). ViewingPane unmounts when Settings / another screen
  // takes over and Back re-fetches the case, so its "case changed → land on
  // Overview" rule fired on every return. It now restores THIS section when
  // the case re-opened is the one memoised. Written by setActiveCaseSection
  // (whenever a case is open) and by restoreView (from the Back/Forward
  // descriptor's caseSection). Cleared on sign-out.
  caseSectionMemo: { caseId: string; section: CaseSectionKey } | null
  // taskIntentId: which task row to scroll to + transiently highlight
  // on next Tasks view mount. Distinct from pendingTaskId (which opens
  // the edit form) — this one leaves the list-view intact and just
  // draws the eye. TasksView consumer clears it once the highlight
  // timeout starts.
  taskIntentId: string | null
  setTaskIntentId: (id: string | null) => void
  // pendingTasksOverdue: one-shot flag set by the dashboard OVERDUE
  // card just before it navigates to the Tasks view. TasksView's mount
  // consumer reads it, starts with the OVERDUE filter ON (which itself
  // forces status=open + self-scope), then clears the flag so a later
  // MANUAL open of Tasks does NOT start overdue. Mirrors the
  // pendingTaskId / taskIntentId set-before-nav, consume-and-clear
  // discipline above.
  pendingTasksOverdue: boolean
  setPendingTasksOverdue: (v: boolean) => void
  // Same one-shot hand-off for the dashboard DUE SOON tile: Tasks opens with
  // the "Due in 7 days" window on.
  pendingTasksDueSoon: boolean
  setPendingTasksDueSoon: (v: boolean) => void
  // Set by ClientSearchView's per-client Tasks section when the
  // user clicks "+ New task" on a selected client — TasksView's
  // mount-time consumer opens the create form with this client
  // preselected as the Link to value, then clears the slot.
  pendingNewTaskClientId: string | null
  setPendingNewTaskClientId: (id: string | null) => void
  // Set by the Calendar day-panel's "New task" — the clicked day as a
  // 'YYYY-MM-DD' string. TasksView flips to create-mode and seeds the
  // form's due-date from this slot, then clears it. Mirrors
  // pendingNewTaskClientId (they can be set together).
  pendingNewTaskDueDate: string | null
  setPendingNewTaskDueDate: (date: string | null) => void
  // Diary click-to-add: optional 'HH:mm' time paired with
  // pendingNewTaskDueDate when the user clicks an HOUR slot on the day/week
  // time-grid (null = clicked the all-day strip → whole-day task). TasksView
  // combines date+time into the created task's dueDate, then clears it.
  pendingNewTaskDueTime: string | null
  setPendingNewTaskDueTime: (time: string | null) => void
  // Set by the Calendar day-panel's "New deadline" (after the user picks
  // a case) — the clicked day as 'YYYY-MM-DD'. DeadlinesSection seeds its
  // add-form due-date from this slot on mount, then clears it. Deadlines
  // are case-scoped, so the panel navigates to the chosen case first.
  pendingNewDeadlineDueDate: string | null
  setPendingNewDeadlineDueDate: (date: string | null) => void
  // The currently-selected client in ClientSearchView's detail mode.
  // Lifted from local React state to the store so the selection
  // survives ClientSearchView unmount/remount (e.g. when the user
  // opens a case, then clicks ← Back from Case Detail). Without
  // this lift, selectedClient would reset to null on remount and
  // the detail branch's `view === 'detail' && selectedClient` gate
  // would short-circuit → blank screen.
  selectedClient: Client | null
  setSelectedClient: (client: Client | null) => void
  // Stage 2a: partial-update mutation for the selectedClient slot —
  // mirrors updateCurrentCaseClient. ClientSearchView's editable
  // Client Details section calls this on every keystroke; useClientAutoSave
  // then subscribes (via its expanded selector that falls back to
  // selectedClient when there's no currentCase) and PATCHes /api/clients/:id
  // after the 1s debounce. No-op when selectedClient is null so callers
  // don't need to guard.
  updateSelectedClient: (changes: Partial<Client>) => void
  // Records which view opened the currently-viewing case. Every
  // open-case trigger sets this BEFORE setCurrentCaseById; Case
  // Detail's ← Cases / wordmark Back handler reads it and re-mounts
  // the matching takeover before clearing the case. 'caseList' (or
  // null) falls through to CaseListScreen.
  caseOpenedFrom: CaseOpenedFrom | null
  setCaseOpenedFrom: (source: CaseOpenedFrom | null) => void
  // Case-redesign Stage 1 — quick-view modal re-open plumbing.
  //   snapshotQuickViewCaseId: transient stamp read by snapshotCurrentView
  //     when building the caseList descriptor, so a "Full case →" from the
  //     modal records the modal on the trail entry. Set just before
  //     pushViewHistory, cleared right after.
  //   pendingQuickViewCaseId: set by restoreView when a stamped caseList
  //     entry is restored (Back); CaseListScreen consumes it (one-shot) to
  //     re-open the quick-view modal for that case.
  snapshotQuickViewCaseId: string | null
  setSnapshotQuickViewCaseId: (id: string | null) => void
  pendingQuickViewCaseId: string | null
  setPendingQuickViewCaseId: (id: string | null) => void
  // Which view opened a task in TasksView's form mode. TasksView's
  // ← Back handler switches on this to route back to Calendar OR
  // ClientSearchView's detail mode instead of falling through to
  // the task list. null = no upstream — Back closes the form only.
  taskOpenedFrom: TaskOpenedFrom
  setTaskOpenedFrom: (source: TaskOpenedFrom) => void
  tasks: Task[] | null                 // null = not yet loaded
  tasksLoading: boolean
  tasksError: string | null
  loadTasks: (filters?: ListTasksFilters) => Promise<void>
  createTask: (payload: CreateTaskPayload) => Promise<Task>
  updateTask: (id: string, payload: UpdateTaskPayload) => Promise<Task>
  deleteTask: (id: string) => Promise<void>
  // clientSearchMode: which sub-view of ClientSearchView is active
  // ('search' results vs 'detail' single-client overview). Lifted
  // from local state in ClientSearchView so the sidebar can force
  // it back to 'search' when the user re-clicks Client Search while
  // already inside the zone — otherwise showClientSearch is already
  // true and the click would be a silent no-op.
  clientSearchMode: 'search' | 'detail'
  setClientSearchMode: (mode: 'search' | 'detail') => void
  // ── SIDEBAR STATE ──────────────────────────────────────
  // sidebarMobileOpen: drives the mobile slide-in overlay only —
  // never persisted (closing on every reload is the right UX).
  // Setting it false is also part of clearAuth so a logout while
  // the overlay is open doesn't leave it visible behind the login
  // screen.
  //
  // sidebarCollapsed (previously here as a boolean slot with
  // localStorage plumbing) migrated to
  // viewingPaneToggles['sidebar.collapsed'] — Sidebar.tsx now
  // consumes it via usePersistedToggle so the preference is
  // per-user and cross-device.
  sidebarMobileOpen: boolean
  setSidebarMobileOpen: (open: boolean) => void
  // ── RECENT CASES (sidebar Phase 2a) ────────────────────
  // Drives the sidebar's "CASES" zone. null = not loaded yet (renders
  // "Loading..."); [] = loaded with zero results (renders "No recent
  // cases"); RawCase[] = list. Stored as RawCase rather than mapped
  // Case so we skip the ~150-line section mapping in setCurrentCaseById
  // — sidebar rows only need id / clientForename / clientSurname /
  // updatedAt, all of which are flat on the raw row.
  recentCases: RawCase[] | null
  setRecentCases: (cases: RawCase[] | null) => void
  // Propagate a case rename to every in-memory surface the case-list row
  // patch does NOT touch: the open-case header (currentCase.caseName), the
  // sidebar Recent list (recentCases carry caseName; its display is hybrid),
  // and the sidebar Favourites CACHED label (a denormalised copy) — the
  // last also persisted via the add-favourite upsert so it survives a
  // reload. `displayName` is the already-resolved hybrid (new caseName,
  // else the client name) used to rebuild the favourite label.
  applyCaseRename: (caseId: string, caseName: string | null, favouriteLabel: string) => void
  // Session rename registry: caseId → its current caseName (null = cleared,
  // reverts to the client name). applyCaseRename writes here so surfaces
  // that fetch their OWN case rows into LOCAL state — e.g. the dashboard
  // "Jump back in" day-stepper (JumpBackInCard) — can overlay the latest
  // name immediately, without the rename having to reach into their state.
  // The backend row is already updated, so the overlay only bridges until
  // that surface next refetches (which returns the same fresh name).
  renamedCaseNames: Record<string, string | null>
  loadRecentCases: () => Promise<void>
  // ── CALENDAR (sidebar Phase 2c) ───────────────────────
  // Drives the sidebar's Calendar zone. null = not loaded yet,
  // [] = loaded with zero results (empty state shown),
  // CalendarEntry[] = list. Mirrors recentCases's tri-state pattern.
  calendarEntries: CalendarEntry[] | null
  setCalendarEntries: (entries: CalendarEntry[] | null) => void
  loadCalendarEntries: () => Promise<void>
  // Non-null when the last calendar load FAILED — so the view can show a
  // retry banner instead of a misleading "No deadlines yet" empty state.
  calendarError: string | null
  // ── DOCUMENT SEARCH (sidebar Phase 2d) ────────────────
  // Drives the sidebar's Document Search zone. null = no search
  // active (initial empty state), [] = search ran with zero results,
  // DocumentSearchResult[] = matches. Mirrors recentCases /
  // calendarEntries tri-state.
  documentSearchResults: DocumentSearchResult[] | null
  setDocumentSearchResults: (results: DocumentSearchResult[] | null) => void
  runDocumentSearch: (options: { q?: string; caseId?: string; clientId?: string }) => Promise<void>
  // Non-null when the last document search FAILED — so the view shows a
  // retry banner instead of a misleading "No documents found" empty state.
  documentSearchError: string | null
  // ── ONBOARDING / DERIVED STATE ─────────────────────────
  // Set by CaseListScreen after a successful fetch. Used by the
  // case-detail onboarding prompts to detect "this is the firm's only
  // case". null means we haven't fetched yet.
  firmCaseCount: number | null
  setFirmCaseCount: (count: number | null) => void
  // ── AUTO-SAVE ERROR ────────────────────────────────────
  // Set by useAutoSave's runSave on failure, cleared on success.
  // SaveIndicator subscribes to it and shows the error visual when
  // non-null. null = no error (default healthy state).
  // Counts clicks on Clients in the nav: the screen goes back to the results.
  clientNavTick: number
  bumpClientNav: () => void
  saveError: Error | null
  setSaveError: (error: Error | null) => void
  // Optimistic-concurrency conflict message (a 409 on save: someone else
  // saved the record first). Surfaced by a global SaveConflictToast and
  // auto-cleared; null = no conflict. Distinct from saveError (a hard save
  // failure) — a conflict is a non-destructive warning, not a red error.
  saveConflict: string | null
  setSaveConflict: (message: string | null) => void
  // ── CASE STATE ──────────────────────────────────────────
  currentCase: Case | null
  // True while setCurrentCaseById is fetching a case. ViewingPane shows a
  // spinner (not the CaseListScreen fallback) when this is true AND
  // currentCase is null — so async case nav (Back, cold-open) never
  // flashes the case list. Null-gated so refreshing an open case is silent.
  isCaseLoading: boolean
  viewingPaneToggles: ViewingPaneToggles
  userRole: UserRole
  // preloadedRaw: an already-fetched RawCase (e.g. the quick-view modal just
  // fetched it) — when passed, the getCaseById round-trip is SKIPPED and the
  // case shell renders immediately from it. Satellite fetches (witnesses,
  // deadlines, TPI, files) still run in the background. Omitted → normal fetch.
  // onShown fires the moment the case itself is in the store (currentCase set)
  // — BEFORE the satellite loads (witnesses / deadlines / TPI / files) settle.
  // The returned promise still resolves only when everything has settled, so
  // existing awaiters are unchanged; callers that should switch view as soon as
  // the case can paint (client page, ?caseId cold-open) use onShown.
  setCurrentCaseById: (id: string, preloadedRaw?: RawCase, onShown?: () => void) => Promise<void>
  updateCase: (updates: Partial<Case>) => void
  // Stage 2b removed updateCurrentCaseClient — the case-page Client
  // Details editor now writes to the case-row SNAPSHOT (currentCase.
  // clientDetails) via updateCase, not the live Client. The live Client
  // is edited from ClientSearch only (updateSelectedClient, persisted
  // by useClientAutoSave) and propagated to cases via sync-client
  // (a later stage).
  clearCurrentCase: () => void
  toggleHeading: (heading: keyof ViewingPaneToggles) => void
  setToggles: (toggles: ViewingPaneToggles) => void
  // Generic setter for dot-namespaced per-user layout preferences
  // (usePersistedToggle backing action). Merges the new key into the
  // existing map (does NOT replace it), then schedules the same
  // debounced updateMeApi save as toggleHeading. Distinct from
  // toggleHeading only because the key type is `string` (arbitrary)
  // rather than `keyof ViewingPaneToggles` (the 17 known heading
  // keys).
  setToggle: (key: string, value: boolean) => void
  finaliseSetup: () => void
  // ── WITNESSES ───────────────────────────────────────────
  // Keyed by caseId so we can keep multiple cases' witnesses cached
  // if needed, though in practice we only ever populate the current
  // case. Falsy lookup = "not loaded yet" — empty array = "loaded,
  // no witnesses". WitnessesSection treats both the same for display
  // but distinguishes for fetch-on-mount logic.
  witnesses: Record<string, Witness[]>
  setWitnesses: (caseId: string, witnesses: Witness[]) => void
  addWitness: (caseId: string, witness: Witness) => void
  updateWitness: (witness: Witness) => void
  removeWitness: (caseId: string, witnessId: string) => void
  // ── CASE DEADLINES ────────────────────────────────────────
  // Per-case keyed map mirroring the witnesses slice exactly.
  // Falsy lookup = "not loaded yet" — empty array = "loaded, no
  // deadlines". DeadlinesSection reads from caseDeadlines[id] ?? [].
  caseDeadlines: Record<string, CaseDeadline[]>
  setCaseDeadlines: (caseId: string, deadlines: CaseDeadline[]) => void
  addCaseDeadline: (caseId: string, deadline: CaseDeadline) => void
  updateCaseDeadline: (deadline: CaseDeadline) => void
  removeCaseDeadline: (caseId: string, deadlineId: string) => void
  // ── FILES ─────────────────────────────────────────────────
  // Same keying / semantics as witnesses above. R2-backed file
  // attachments per case. No updateFile action — file rows are
  // immutable post-upload (metadata is set at upload time and
  // never changes), so the slice only needs set / add / remove.
  files: Record<string, FileAttachment[]>
  setFiles: (caseId: string, files: FileAttachment[]) => void
  addFile: (caseId: string, file: FileAttachment) => void
  removeFile: (caseId: string, fileId: string) => void
  // ── TPI CONTACTS (FIRM-WIDE DIRECTORY) ───────────────────
  // Flat array — directory is firm-wide, not per-case. Refreshed
  // every time a case is opened (cheap; the list is small) so any
  // contact a colleague added on another browser shows up.
  tpiContacts: TpiContact[]
  setTpiContacts: (list: TpiContact[]) => void
  addTpiContact: (contact: TpiContact) => void
  replaceTpiContact: (contact: TpiContact) => void
  // ── CLIENTS ─────────────────────────────────────────────
  // Firm-wide client list. Phase 3's Client picker UI will call
  // loadClients on mount and use the array for autocomplete.
  clients: Client[]
  clientsLoading: boolean
  clientsError: string | null
  loadClients: (search?: string, sort?: string) => Promise<void>
  createClient: (payload: CreateClientPayload) => Promise<Client>
  updateClient: (id: string, payload: UpdateClientPayload & { baseUpdatedAt?: string }) => Promise<Client>
  // Deletes a client and removes it from the firm-wide clients[] array.
  // Throws on failure (notably the backend's 409 when the client still
  // has linked cases) — callers handle the error to surface the
  // message in their own UX.
  deleteClient: (id: string) => Promise<void>
}
const buildDefaultToggles = (): ViewingPaneToggles => {
  const toggles = {} as ViewingPaneToggles
  VIEWING_PANE_HEADINGS.forEach((h) => { toggles[h.key] = h.defaultVisible })
  return toggles
}

// Merges a saved toggles blob (from getMe / setAuthUser) over the
// hardcoded defaults, runtime-filtering to booleans-on-known-keys.
// The type is EARNED (real per-entry checks), not asserted — and it
// hardens against a malformed stored blob (pre-validator writes,
// manual DB edits, a future backend regression) crashing the seed.
// Fixes TS2322 at the two seed sites below: AuthUser declares
// viewingPaneToggles as Partial<ViewingPaneToggles>, so a bare
// spread yields `boolean | undefined` values that don't satisfy
// ViewingPaneToggles' strict boolean index signature — this helper
// resolves the boundary once and returns a clean ViewingPaneToggles.
const seedToggles = (saved: unknown): ViewingPaneToggles => {
  const base = buildDefaultToggles()
  if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
    for (const [k, v] of Object.entries(saved as Record<string, unknown>)) {
      // Keep every BOOLEAN entry from the saved blob — NOT just the
      // VIEWING_PANE_HEADINGS keys. The previous `k in base` filter
      // silently DROPPED namespaced layout prefs like 'sidebar.collapsed'
      // (and 'case.*.collapsed'), because those aren't heading keys. That
      // meant every re-seed (setAuth on login, setAuthUser on the mount
      // getMe) stripped the sidebar-collapsed pref → the sidebar reset to
      // OPEN. Worst case: getMe resolved just AFTER the user collapsed and
      // re-seeded, springing the sidebar back open ("collapse doesn't
      // stick"). ViewingPaneToggles has a string index signature, so
      // namespaced keys are type-valid; typeof-boolean still filters junk.
      if (typeof v === 'boolean') (base as Record<string, boolean>)[k] = v
    }
  }
  return base
}
// ── viewingPaneToggles auto-save ───────────────────────────
// Module-scope singletons mirror the useAutoSave pattern.
// Debounced 2 s — user often clicks multiple toggles in quick
// succession, so we batch into one PATCH /api/auth/me.
let togglesSaveTimer: ReturnType<typeof setTimeout> | null = null
let togglesPendingPayload: Partial<ViewingPaneToggles> | null = null
const scheduleTogglesSave = (toggles: ViewingPaneToggles): void => {
  togglesPendingPayload = toggles
  if (togglesSaveTimer) clearTimeout(togglesSaveTimer)
  togglesSaveTimer = setTimeout(() => {
    const payload = togglesPendingPayload
    togglesPendingPayload = null
    togglesSaveTimer = null
    if (!payload) return
    updateMeApi({ viewingPaneToggles: payload }).catch((err: unknown) => {
      // Silent failure — layout preferences must not surface a user-
      // facing error. Reasons the PATCH can fail today: (1) the
      // backend F-17 validator tightened this endpoint and the
      // frontend sent an unexpected shape, (2) offline / network,
      // (3) transient 5xx. In every case the toggle keeps working
      // in-session (state is optimistic in the store); it just
      // doesn't survive a refresh. One console.warn per failure
      // so it's greppable if the whole persistence layer is broken;
      // NEVER setSaveError (which would flash the SaveIndicator).
      // eslint-disable-next-line no-console
      console.warn('[viewingPaneToggles] save failed:', err)
    })
  }, 2000)
}

// Per-item favourite sync state for toggleFavourite (race-safe toggle):
// the latest DESIRED state, the server's real row, and the in-flight worker.
const favSync = new Map<string, { want: boolean; label: string; subSection: string | null; server: Favourite | null; running: Promise<void> | null }>()
// Overlay the in-flight toggles' desired state onto a freshly loaded list.
const overlayPendingFavourites = (list: Favourite[]): Favourite[] => {
  let out = list
  favSync.forEach((st, key) => {
    const sep = key.indexOf(':')
    const itemType = key.slice(0, sep)
    const itemId = key.slice(sep + 1)
    const has = out.some((f) => f.itemType === itemType && f.itemId === itemId)
    if (!st.want && has) out = out.filter((f) => !(f.itemType === itemType && f.itemId === itemId))
    else if (st.want && !has) {
      out = [...out, st.server ?? {
        id: `temp-${key}-${Date.now()}`, itemType: itemType as Favourite['itemType'], itemId,
        subSection: st.subSection, label: st.label, sortOrder: out.length, createdAt: new Date().toISOString(),
      }]
    }
  })
  return out
}

// Monotonic open counter for setCurrentCaseById (latest-open-wins).
let caseOpenSeq = 0

// The saved session, read ONCE at start-up. A saved user that isn't readable
// JSON (or isn't an object) used to throw while the store was created — a
// blank app. Now the bad value (and its token) is cleared and the app starts
// logged out, so the user just signs in again.
const readStartUpSession = (): { token: string | null; user: AuthUser | null } => {
  try {
    const token = localStorage.getItem('casevo_token')
    const stored = localStorage.getItem('casevo_user')
    if (!stored) return { token, user: null }
    const user: unknown = JSON.parse(stored)
    if (!user || typeof user !== 'object' || Array.isArray(user)) throw new Error('saved user is not an object')
    return { token, user: user as AuthUser }
  } catch {
    try {
      localStorage.removeItem('casevo_user')
      localStorage.removeItem('casevo_token')
    } catch { /* storage unavailable — nothing to clear */ }
    return { token: null, user: null }
  }
}
const startUpSession = readStartUpSession()

export const useCasevoStore = create<CasevoStore>((set, get) => ({
  // ── AUTH ────────────────────────────────────────────────
  authToken: startUpSession.token,
  authUser: startUpSession.user,
  isAuthenticated: !!startUpSession.token,
  setAuth: (token: string, user: AuthUser) => {
    // Persist to localStorage so session survives page refresh
    localStorage.setItem('casevo_token', token)
    localStorage.setItem('casevo_user', JSON.stringify(user))
    // userRole DERIVES from the real, server-provided role — it is NOT
    // user-settable (the self-serve switcher was removed). Case-detail
    // section gating reads userRole; deriving it here (and in setAuthUser
    // / init / clearAuth) makes authUser.role the single source of admin
    // status client-side. Server-side requireAdmin remains the real guard.
    set({ authToken: token, authUser: user, isAuthenticated: true, userRole: user.role === 'admin' ? 'admin' : 'user' })
    // Seed viewing-pane toggles from the user's saved preferences.
    // Defaults first, then the user's overrides win where present.
    // Routed through seedToggles so the boundary earns its
    // ViewingPaneToggles type via runtime-filtering — see helper.
    set({ viewingPaneToggles: seedToggles(user.viewingPaneToggles) })
  },
  setAuthUser: (user: AuthUser) => {
    // The account's theme choice follows the user across devices.
    syncAccountAppearance(user)
    // Update user in store and localStorage (token stays unchanged)
    localStorage.setItem('casevo_user', JSON.stringify(user))
    // Re-derive userRole from the freshly-fetched role (getMe hydrate) so
    // a role change on the server takes effect on next load — see setAuth.
    // Did the role / effective permissions actually change vs what we had?
    const prev = get().authUser
    const sig = (u: AuthUser | null): string => (u ? `${u.role}|${JSON.stringify(u.effectivePermissions ?? {})}` : '')
    const permsChanged = !!prev && sig(prev) !== sig(user)
    // ★ Keep the user's OWN entry in the central firmUsers map in step with
    // authUser (name / avatar), so every screen that renders people by id
    // shows the new name the instant a Settings save lands — no refresh.
    const firmUsersSynced = get().firmUsers.map((u) => u.id === user.id
      ? { ...u, firstName: user.firstName, lastName: user.lastName, avatarColour: user.avatarColour ?? u.avatarColour, avatarStyle: user.avatarStyle ?? u.avatarStyle, avatarPhotoUrl: user.avatarPhotoUrl !== undefined ? user.avatarPhotoUrl : u.avatarPhotoUrl }
      : u)
    set({
      authUser: user,
      userRole: user.role === 'admin' ? 'admin' : 'user',
      firmUsers: firmUsersSynced,
      ...(permsChanged ? { permissionsVersion: get().permissionsVersion + 1 } : {}),
    })
    // Merge the fetched prefs OVER the CURRENT in-session toggles (server
    // wins for keys it carries; keys only the store has are preserved).
    // This matters for the mount getMeApi refresh: it fires on load and
    // resolves a few hundred ms later — if the user collapsed the sidebar
    // in that window, the change is optimistic in the store but not yet
    // saved (2s-debounced PATCH), so the server user doesn't have it. A
    // plain seedToggles(user…) would build a FRESH map from the server and
    // drop that unsaved key, springing the sidebar back open ("collapse
    // doesn't stick"). Spreading server LAST over the current toggles keeps
    // server as source-of-truth for anything it knows while preserving the
    // in-session-only change. (seedToggles now also keeps namespaced keys
    // like 'sidebar.collapsed', which the old `k in base` filter stripped.)
    set((state) => ({
      viewingPaneToggles: seedToggles({
        ...state.viewingPaneToggles,
        ...(user.viewingPaneToggles ?? {}),
      }),
    }))
  },
  clearAuth: () => {
    // (Batch C) drop the previous session's cached My Work lists.
    clearTaskScopeCache()
    clearDocumentSearch()
    localStorage.removeItem('casevo_token')
    localStorage.removeItem('casevo_user')
    // Stage B: showDashboard reset to TRUE (not false) so re-login
    // lands on Dashboard just like first-login. Every other takeover
    // flag stays reset to false — Dashboard has render precedence in
    // App.tsx so it doesn't need siblings to be true to appear.
    set({ authToken: null, authUser: null, isAuthenticated: false, userRole: 'user', currentCase: null, showDashboard: true, showSettings: false, showAdmin: false, showClientSearch: false, showCalendar: false, showDocumentSearch: false, showBilling: false, showTasks: false, clientSearchMode: 'search', firmCaseCount: null, saveError: null, saveConflict: null, witnesses: {}, caseDeadlines: {}, files: {}, tpiContacts: [], clients: [], clientsLoading: false, clientsError: null, authView: 'login', pendingResetToken: null, pendingInviteToken: null, pendingCaseId: null, sidebarMobileOpen: false, recentCases: null, calendarEntries: null, calendarError: null, documentSearchResults: null, documentSearchError: null, tasks: null, tasksLoading: false, tasksError: null, pendingTaskId: null, pendingClientId: null, pendingClientSection: null, pendingClientActivityCase: null, pendingBill: null, pendingRaiseBillCaseId: null, clientSectionMemo: null, caseSectionMemo: null, pendingAdminMemberId: null, pendingNewTaskClientId: null, selectedClient: null, caseOpenedFrom: null, taskOpenedFrom: null, caseInitialSection: null, activeCaseSection: null, taskIntentId: null, placeParams: {}, isRestoringView: false, firmColourScheme: {}, userColourScheme: {}, caseHistoryVisibility: null, favourites: null, favouritesError: null, firmUsers: [], firmUsersLoaded: false, firmUsersLoading: false })
  },
  // ── AUTH VIEW ──────────────────────────────────────────
  authView: 'login',
  pendingResetToken: null,
  pendingInviteToken: null,
  permissionsVersion: 0,
  setAuthView: (view: AuthView) => set({ authView: view }),
  setPendingResetToken: (token: string | null) => set({ pendingResetToken: token }),
  setPendingInviteToken: (token: string | null) => set({ pendingInviteToken: token }),
  pendingCaseId: null,
  setPendingCaseId: (id: string | null) => set({ pendingCaseId: id }),
  // ── NAVIGATION ─────────────────────────────────────────
  showSettings: false,
  setShowSettings: (show: boolean) => set({ showSettings: show }),
  showAdmin: false,
  setShowAdmin: (show: boolean) => set({ showAdmin: show }),
  showClientSearch: false,
  setShowClientSearch: (show: boolean) => set({ showClientSearch: show }),
  showCalendar: false,
  setShowCalendar: (show: boolean) => set({ showCalendar: show }),
  showDocumentSearch: false,
  setShowDocumentSearch: (show: boolean) => set({ showDocumentSearch: show }),
  showBilling: false,
  // Takeover open (no history push — navigateTo({ view: 'billing' }) is the
  // navigation; this is just the state change).
  openBilling: () => { get().closeAllTakeovers(); set({ showBilling: true }) },
  // ── Tasks zone ────────────────────────────────────────────
  showTasks: false,
  setShowTasks: (show) => set({ showTasks: show }),
  // ── Dashboard (landing) ───────────────────────────────────
  // Stage B: default landing view. Initial value TRUE (not false —
  // that's what makes Dashboard the post-login home). clearAuth
  // re-seeds this to `true` on logout so re-login also lands here.
  showDashboard: true,
  setShowDashboard: (show: boolean) => set({ showDashboard: show }),
  // Stage B: single-line "close every takeover" — the mutual-
  // exclusion primitive nav handlers now build on. Includes
  // showDashboard so opening a takeover from Dashboard implicitly
  // closes Dashboard (its render precedence in App.tsx would
  // otherwise keep DashboardView on top).
  //
  // State only: it no longer pushes history. Every "go to X" goes
  // through navigation/navigate.ts navigateTo, which pushes the entry
  // (with the place being left) and then calls this.
  closeAllTakeovers: () => {
    set(() => ({
      showDashboard: false,
      showSettings: false,
      showAdmin: false,
      showClientSearch: false,
      showCalendar: false,
      showDocumentSearch: false,
      showBilling: false,
      showTasks: false,
      // Leaving a case detail for a takeover view (dashboard / clients /
      // settings / etc.) means you're no longer VIEWING a case, so clear
      // currentCase. This is the single choke point every nav-away handler
      // builds on, so it fixes BOTH the stuck sidebar highlight (driven by
      // currentCase?.id) AND lingering presence (currentCase→null makes
      // usePresenceConnection send "leave" → your avatar disappears for
      // others). Ordered AFTER pushViewHistory() above, so the Back
      // snapshot still captures 'caseDetail'; Back restores the case via
      // restoreView's re-fetch, so nothing depends on currentCase
      // surviving this call. "last opened case" (recent cases) is tracked
      // server-side via CaseAccess, NOT off currentCase — so unaffected.
      currentCase: null,
    }))
  },
  // ── Back-restore guard ─────
  // Initial value: false. Flipped true only for the duration of a
  // popstate handler (App.tsx). Reset in clearAuth to guarantee a
  // fresh mount starts un-guarded even if a prior session ended
  // mid-popstate somehow.
  isRestoringView: false,
  setIsRestoringView: (v: boolean) => set({ isRestoringView: v }),
  // ── Case-list tab (Stage 4c) ────────────────────────────
  // Lazy IIFE reads localStorage on first store construction so the
  // last-chosen tab survives a hard page reload. Format: '1' means
  // Recent (true), '0' means All (false). Missing / malformed keys
  // fall back to `true` — the solicitor-spec default of "what I'm
  // working on today". Direct localStorage.* call (no window guard)
  // matches every other localStorage read in this file (e.g.
  // clearAuth's removeItem calls, and CaseListScreen's sortKey
  // initializer at L142) — the app is browser-only, no SSR path.
  caseListRecentOnly: (() => {
    const stored = localStorage.getItem('casevo_case_list_recent_only')
    if (stored === '1') return true
    if (stored === '0') return false
    return true
  })(),
  setCaseListRecentOnly: (v) => {
    // Resolve functional-form updates against the CURRENT store
    // value (not a stale closure) so `setRecentOnly((prev) => !prev)`
    // works verbatim from CaseListScreen. Then write-through to
    // localStorage BEFORE the store set() so parallel tabs / a
    // mid-reload race can read the same value the current page's UI
    // is about to render with. Direct write, not debounced — the
    // setter is called at most on a toggle click, not per-keystroke,
    // so no debounce is warranted.
    const next = typeof v === 'function' ? v(get().caseListRecentOnly) : v
    localStorage.setItem('casevo_case_list_recent_only', next ? '1' : '0')
    set({ caseListRecentOnly: next })
  },
  // ── Navigation history ────────────────────────────────────
  // Seeded from the entry we loaded on: after a refresh the entries behind
  // it still exist (each with its place), so depth carries on from there.
  currentSeq: (() => {
    const st = window.history.state as { casevoSeq?: unknown } | null
    return st && typeof st.casevoSeq === 'number' ? st.casevoSeq : 0
  })(),
  setCurrentSeq: (currentSeq) => set({ currentSeq }),
  placeParams: {},
  placeNonce: 0,
  setPlaceParam: (key, value) => {
    const cur = get().placeParams
    if (value === null ? !(key in cur) : cur[key] === value) return
    const next = { ...cur }
    if (value === null) delete next[key]
    else next[key] = value
    set({ placeParams: next })
  },
  // Pure read of current state → descriptor for whatever view
  // is showing right now. Precedence MIRRORS App.tsx renderMain
  // L236-245 exactly, in this order:
  //   1. showDashboard
  //   2. showTasks
  //   3. showDocumentSearch
  //   4. showCalendar
  //   5. showClientSearch      (splits into clientSearch |
  //                             clientDetail based on
  //                             clientSearchMode + selectedClient)
  //   6. showAdmin
  //   7. showSettings
  //   8. currentCase           → caseDetail
  //   9. fallthrough           → caseList
  // NB: mutual exclusion via closeAllTakeovers means only one
  // takeover flag is ever true, so precedence only matters as a
  // race tiebreaker — but it MUST match renderMain so the
  // descriptor never diverges from what the user actually sees.
  //
  // The spec Jackson gave listed a DIFFERENT precedence
  // (dashboard → settings → admin → clientSearch → calendar →
  // documentSearch → tasks); flagged in the report. This impl
  // follows the actual renderMain cascade per the "confirm the
  // exact precedence against renderMain" clause.
  snapshotCurrentView: () => {
    const base = ((): ViewDescriptor => {
    const s = get()
    if (s.showDashboard) return { view: 'dashboard' }
    // A task opened from the calendar is a MODAL over the still-mounted
    // calendar (App renders both) — the place is still the calendar.
    if (s.showTasks && !(s.showCalendar && s.taskOpenedFrom === 'calendar')) return { view: 'tasks' }
    if (s.showDocumentSearch) return { view: 'documentSearch' }
    if (s.showBilling) return { view: 'billing' }
    if (s.showCalendar) return { view: 'calendar' }
    if (s.showClientSearch) {
      return s.clientSearchMode === 'detail' && s.selectedClient
        ? {
            view: 'clientDetail',
            clientId: s.selectedClient.id,
            ...(s.clientSectionMemo && s.clientSectionMemo.clientId === s.selectedClient.id ? { clientSection: s.clientSectionMemo.section } : {}),
          }
        : { view: 'clientSearch' }
    }
    if (s.showAdmin) return { view: 'admin' }
    if (s.showSettings) return { view: 'settings' }
    if (s.currentCase) {
      return {
        view: 'caseDetail',
        caseId: s.currentCase.id,
        ...(s.activeCaseSection ? { caseSection: s.activeCaseSection } : {}),
      }
    }
    // Stage 4c: capture the caseList tab so Back-restore reproduces
    // the exact tab the user was on. Encoded as 'recent' | 'all'
    // (rather than the boolean caseListRecentOnly is stored as) so
    // the descriptor reads correctly and is future-proof if we
    // ever add a third tab.
    return {
      view: 'caseList',
      listTab: s.caseListRecentOnly ? 'recent' : 'all',
      // Stamp the quick-view modal onto this trail entry when a "Full case →"
      // was launched from it (snapshotQuickViewCaseId set just before this
      // push). Absent otherwise, so ordinary list snapshots are unchanged.
      ...(s.snapshotQuickViewCaseId ? { quickViewCaseId: s.snapshotQuickViewCaseId } : {}),
    }
    })()
    // The mounted screen's sub-place (open bill, section, filter…).
    const params = get().placeParams
    return Object.keys(params).length ? { ...base, params: { ...params } } : base
  },
  // One browser entry per navigation, each carrying its own place. The
  // current entry is re-stamped with the place being LEFT first (so Back
  // lands exactly there), then the new entry is pushed (its place is filled
  // in by the place sync as the destination renders). Short-circuits while
  // a popstate restore runs, so restoring never adds entries.
  pushViewHistory: (leaving) => {
    if (get().isRestoringView) return
    const from = leaving ?? get().snapshotCurrentView()
    const url = `/${placeToSearch(from)}`
    const cur = get().currentSeq
    window.history.replaceState({ casevoSeq: cur, place: from }, '', url)
    const seq = cur + 1
    window.history.pushState({ casevoSeq: seq, place: from }, '', url)
    set({ currentSeq: seq })
  },
  // restoreView: apply a descriptor by inline-writing the 7
  // takeover flags in ONE set(). Deliberately does not call
  // closeAllTakeovers — that would push another viewHistory
  // entry and add a stray browser history entry inside the
  // very popstate we're handling. Precedence-agnostic: only
  // one flag is ever true after the set(), so App.tsx's
  // renderMain cascade lands on the intended view.
  //
  // Stage 3: caseDetail + clientDetail now truly restore. See the
  // interface docblock for semantics + failure fallbacks. The
  // function is async so the popstate caller can hold
  // isRestoringView across the entire operation (including the
  // await'd fetch). Sync branches return a resolved promise
  // implicitly; async branches await their fetches inline.
  restoreView: async (descriptor) => {
    // The place's own sub-place params replace the current ones; the nonce
    // tells a screen that stays mounted (same screen, other bill / section)
    // to re-apply them. A screen that mounts reads them as its initial state.
    set((st) => ({ placeParams: { ...(descriptor.params ?? {}) }, placeNonce: st.placeNonce + 1 }))
    const zeroed = {
      showDashboard: false,
      showTasks: false,
      showDocumentSearch: false,
      showBilling: false,
      showCalendar: false,
      showClientSearch: false,
      showAdmin: false,
      showSettings: false,
    }
    // Leaving a case for another screen: no longer viewing it (presence +
    // sidebar highlight) — same as closeAllTakeovers.
    const takeover = { ...zeroed, currentCase: null }
    switch (descriptor.view) {
      case 'billing':
        set({ ...takeover, showBilling: true })
        return
      case 'dashboard':
        set({ ...takeover, showDashboard: true })
        return
      case 'tasks':
        // A plain Tasks place (never the calendar-overlay form).
        set({ ...takeover, showTasks: true, taskOpenedFrom: null })
        return
      case 'documentSearch':
        set({ ...takeover, showDocumentSearch: true })
        return
      case 'calendar':
        set({ ...takeover, showCalendar: true })
        return
      case 'clientSearch':
        set({ ...takeover, showClientSearch: true, clientSearchMode: 'search' })
        return
      case 'admin':
        set({ ...takeover, showAdmin: true })
        return
      case 'settings':
        set({ ...takeover, showSettings: true })
        return
      case 'caseDetail': {
        // Defensive: descriptor.caseId is optional in the type
        // even though snapshotCurrentView always captures it when
        // the branch fires (L611). A missing id shouldn't crash
        // the popstate handler — fall back to the case list.
        const caseId = descriptor.caseId
        if (!caseId) {
          set({ ...zeroed, currentCase: null })
          return
        }
        // Same case already on screen (Back between its sections, or from a
        // bill opened in its Billing section): just switch section — no
        // re-fetch, no skeleton.
        const open = get().currentCase
        if (open && open.id === caseId) {
          set({ ...zeroed })
          if (descriptor.caseSection) get().setActiveCaseSection(descriptor.caseSection)
          return
        }
        // Zero the flags AND clear currentCase up-front so the UI
        // doesn't flash a stale takeover while the fetch is in
        // flight. ViewingPane shows CaseListScreen momentarily
        // (empty currentCase + zero flags → renderMain fallthrough
        // → ViewingPane → CaseListScreen), which is a saner
        // loading state than the previous view's chrome.
        // Seed the per-case section memory from the descriptor so the
        // re-opened case lands on the section the user left (ViewingPane
        // reads caseSectionMemo when the case "changes").
        set({
          ...zeroed,
          currentCase: null,
          ...(descriptor.caseSection ? { caseSectionMemo: { caseId, section: descriptor.caseSection } } : {}),
        })
        try {
          // setCurrentCaseById is the same action every normal
          // case-open path uses — it fetches /api/cases/:id,
          // maps the response into the frontend Case shape, and
          // side-fetches witnesses. Awaiting it means the guard
          // (isRestoringView) held by the popstate caller stays
          // true across the whole open, so any incidental
          // pushViewHistory triggered mid-fetch would still no-op.
          await get().setCurrentCaseById(caseId)
        } catch (err) {
          // Case gone (404 / 403 / network). Do NOT leave a blank
          // screen — the up-front set already put us on caseList,
          // but re-issue the zero to be explicit and to handle
          // any partial state a partial fetch might have left.
          console.error('[restoreView] setCurrentCaseById failed for', caseId, err)
          set({ ...zeroed, currentCase: null })
        }
        return
      }
      case 'clientDetail': {
        const clientId = descriptor.clientId
        if (!clientId) {
          // Malformed descriptor — fall back to search rather
          // than a broken detail pane.
          set({ ...takeover, showClientSearch: true, clientSearchMode: 'search' })
          return
        }
        // Three-tier resolution, cheapest first:
        //   (1) selectedClient in the store is already the target —
        //       closeAllTakeovers does NOT clear it, so the common
        //       case (Dashboard→X→Back-to-clientDetail) hits this
        //       branch and avoids any network round-trip.
        //   (2) clients[] cache from ClientSearchView's loadClients
        //       call — populated if the user has visited the search
        //       list in this session.
        //   (3) getClientByIdApi — direct fetch of the single Client
        //       row from the backend. This mirrors the "normal path"
        //       (ClientSearchView.handleSelectClient takes a Client
        //       object, but that object always came from clients[]
        //       which was itself populated by a list fetch). No
        //       existing single-client-by-id navigation exists in
        //       the app today — getClientByIdApi is the same primitive
        //       used by getMe-style detail requests elsewhere.
        const current = get().selectedClient
        let client: Client | null | undefined =
          current && current.id === clientId
            ? current
            // (Audit batch 3) a walled client's search stub is never a record.
            : get().clients.find((c) => c.id === clientId && !c.restricted)
        if (!client) {
          try {
            client = await getClientByIdApi(clientId)
          } catch (err) {
            // Client gone or unreachable — drop into search mode
            // rather than a broken detail pane. Preserves flow so
            // the user can pick a different client instead of
            // being punted to the case list.
            console.error('[restoreView] getClientByIdApi failed for', clientId, err)
            // (Audit batch 3, H4) Behind an ethical wall → hand the id to the
            // client screen, which shows the "Restricted client" page.
            set({ ...takeover, showClientSearch: true, clientSearchMode: 'search', ...(isRestrictedClientError(err) ? { pendingClientId: clientId } : {}) })
            return
          }
        }
        // Capture the concurrency baseline here too — this path sets
        // selectedClient directly (not via setSelectedClient), so mirror
        // the capture so a restored client can still guard its saves.
        setClientBaseUpdatedAt(client.id, client.updatedAt, 'load-restore')
        // ClientSearchView's remount-refetch effect (L791-796) fires
        // on mount when selectedClient is set and view === 'detail',
        // re-hydrating cases/tasks/activities/documents. So setting
        // just these three fields is sufficient — no need to call
        // handleSelectClient directly from the store.
        set({
          ...takeover,
          showClientSearch: true,
          clientSearchMode: 'detail',
          selectedClient: client,
          // Land on the tab that was showing — ClientSearchView follows the memo
          // (on mount AND when it's already showing this client).
          ...(descriptor.clientSection ? { clientSectionMemo: { clientId: client.id, section: descriptor.clientSection } } : {}),
        })
        return
      }
      case 'caseList':
      default: {
        // Zero all takeovers and drop currentCase so ViewingPane
        // renders the case list.
        //
        // Stage 4c listTab restoration: if the descriptor is a
        // 'caseList' entry AND carries listTab (post-4c trail
        // entries), write caseListRecentOnly back so the remounted
        // CaseListScreen renders the tab the user was on. Older
        // trail entries — or any non-'caseList' fallthrough that
        // lands in the `default` branch — omit listTab, and we
        // deliberately do NOT touch caseListRecentOnly then: leave
        // whatever the current value is (localStorage-persisted
        // last choice or the initial default). Never force-reset
        // to a fixed value here — that would surprise users who
        // reload while on the Recent tab.
        const listTab =
          descriptor.view === 'caseList' ? descriptor.listTab : undefined
        // Case-redesign Stage 1: if this caseList entry was stamped with a
        // quickViewCaseId (Back from a full case that was opened FROM the
        // quick-view modal), hand it to CaseListScreen so it RE-OPENS the
        // modal — Back retraces one step, not two. Null on every other
        // caseList restore, so normal Back lands on the plain list.
        const quickViewCaseId =
          descriptor.view === 'caseList' ? (descriptor.quickViewCaseId ?? null) : null
        if (listTab !== undefined) {
          set({
            ...zeroed,
            currentCase: null,
            caseListRecentOnly: listTab === 'recent',
            pendingQuickViewCaseId: quickViewCaseId,
          })
        } else {
          set({ ...zeroed, currentCase: null, pendingQuickViewCaseId: quickViewCaseId })
        }
        return
      }
    }
  },
  pendingTaskId: null,
  setPendingTaskId: (id) => set({ pendingTaskId: id }),
  pendingCalendarDate: null,
  setPendingCalendarDate: (day) => set({ pendingCalendarDate: day }),
  pendingCalendarAppointmentId: null,
  setPendingCalendarAppointmentId: (id) => set({ pendingCalendarAppointmentId: id }),
  pendingClientId: null,
  setPendingClientId: (id) => set({ pendingClientId: id }),
  pendingClientSection: null,
  setPendingClientSection: (section) => set({ pendingClientSection: section }),
  pendingClientActivityCase: null,
  setPendingClientActivityCase: (v) => set({ pendingClientActivityCase: v }),
  pendingBill: null,
  setPendingBill: (v) => set({ pendingBill: v }),
  billingListTick: 0,
  requestBillingList: () => set((s) => ({ billingListTick: s.billingListTick + 1 })),
  pendingRaiseBillCaseId: null,
  setPendingRaiseBillCaseId: (caseId) => set({ pendingRaiseBillCaseId: caseId }),
  clientSectionMemo: null,
  setClientSectionMemo: (memo) => set({ clientSectionMemo: memo }),
  pendingSettingsSection: null,
  setPendingSettingsSection: (section) => set({ pendingSettingsSection: section }),
  pendingAdminMemberId: null,
  setPendingAdminMemberId: (id) => set({ pendingAdminMemberId: id }),
  // Deep-link intents: both start null; routeByItemType writes them
  // before nav, and the target view's mount effect reads + clears.
  caseInitialSection: null,
  setCaseInitialSection: (section) => set({ caseInitialSection: section }),
  activeCaseSection: null,
  setActiveCaseSection: (section) => {
    const open = get().currentCase
    set(section && open
      ? { activeCaseSection: section, caseSectionMemo: { caseId: open.id, section } }
      : { activeCaseSection: section })
  },
  caseSectionMemo: null,
  taskIntentId: null,
  setTaskIntentId: (id) => set({ taskIntentId: id }),
  pendingTasksOverdue: false,
  setPendingTasksOverdue: (v) => set({ pendingTasksOverdue: v }),
  pendingTasksDueSoon: false,
  setPendingTasksDueSoon: (v) => set({ pendingTasksDueSoon: v }),
  pendingNewTaskClientId: null,
  setPendingNewTaskClientId: (id) => set({ pendingNewTaskClientId: id }),
  pendingNewTaskDueDate: null,
  setPendingNewTaskDueDate: (date) => set({ pendingNewTaskDueDate: date }),
  pendingNewTaskDueTime: null,
  setPendingNewTaskDueTime: (time) => set({ pendingNewTaskDueTime: time }),
  pendingNewDeadlineDueDate: null,
  setPendingNewDeadlineDueDate: (date) => set({ pendingNewDeadlineDueDate: date }),
  selectedClient: null,
  setSelectedClient: (selectedClient) => {
    // Capture the SERVER updatedAt when a freshly-loaded client is set (the
    // optimistic-concurrency baseline). setSelectedClient is only called with
    // server-fetched clients; per-keystroke edits go through
    // updateSelectedClient, which does NOT touch this — so the baseline stays
    // the true server value. (Client edits don't stamp updatedAt locally, but
    // capturing at load keeps the case + client paths symmetric and robust.)
    if (selectedClient) setClientBaseUpdatedAt(selectedClient.id, selectedClient.updatedAt, 'load')
    set({ selectedClient })
  },
  updateSelectedClient: (changes) => set((state) => ({
    selectedClient: state.selectedClient ? { ...state.selectedClient, ...changes } : null,
  })),
  caseOpenedFrom: null,
  setCaseOpenedFrom: (caseOpenedFrom) => set({ caseOpenedFrom }),
  snapshotQuickViewCaseId: null,
  setSnapshotQuickViewCaseId: (id) => set({ snapshotQuickViewCaseId: id }),
  pendingQuickViewCaseId: null,
  setPendingQuickViewCaseId: (id) => set({ pendingQuickViewCaseId: id }),
  taskOpenedFrom: null,
  setTaskOpenedFrom: (taskOpenedFrom) => set({ taskOpenedFrom }),
  tasks: null,
  tasksLoading: false,
  tasksError: null,
  loadTasks: async (filters) => {
    set({ tasksLoading: true, tasksError: null })
    try {
      const data = await listTasksApi(filters)
      set({ tasks: data, tasksLoading: false })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load tasks'
      // eslint-disable-next-line no-console
      console.error('[useCasevoStore] loadTasks failed:', err)
      set({ tasksLoading: false, tasksError: message })
    }
  },
  createTask: async (payload) => {
    const created = await createTaskApi(payload)
    // Prepend the new task into the cached list so the UI updates
    // immediately. Fresh load happens whenever loadTasks is called.
    const current = get().tasks
    if (current !== null) {
      set({ tasks: [created, ...current] })
    }
    return created
  },
  updateTask: async (id, payload) => {
    const updated = await updateTaskApi(id, payload)
    const current = get().tasks
    if (current !== null) {
      set({ tasks: current.map((t) => (t.id === id ? updated : t)) })
    }
    return updated
  },
  deleteTask: async (id) => {
    await deleteTaskApi(id)
    const current = get().tasks
    if (current !== null) {
      set({ tasks: current.filter((t) => t.id !== id) })
    }
    // Success-only (the await above throws on failure, skipping this):
    // the backend cascade has removed any favourite pinning this task,
    // so re-fetch favourites in the background to drop the now-dead row
    // from the sidebar immediately. Fire-and-forget — never blocks the
    // delete UX.
    void get().loadFavourites()
  },
  clientSearchMode: 'search',
  setClientSearchMode: (mode: 'search' | 'detail') => set({ clientSearchMode: mode }),
  // ── SIDEBAR ────────────────────────────────────────────
  // sidebarMobileOpen is always false on init — the mobile overlay
  // should never be open on page load. sidebarCollapsed migrated to
  // viewingPaneToggles['sidebar.collapsed'] (usePersistedToggle in
  // Sidebar.tsx); the previous localStorage slot
  // 'casevo_sidebar_collapsed' is no longer read or written.
  sidebarMobileOpen: false,
  setSidebarMobileOpen: (open: boolean) => set({ sidebarMobileOpen: open }),
  // ── RECENT CASES ───────────────────────────────────────
  // Mirrors loadClients' async-action pattern (no loading flag — we
  // use `null` to mean "not loaded yet" and `[]` to mean "loaded with
  // zero results"). On error, swallow + log + set [] so the sidebar's
  // empty state shows rather than perpetual "Loading...".
  // Colour-cascade schemes. Empty {} until loaded → the render resolves
  // shipped defaults (identical appearance). loadColourSchemes fetches
  // both once; failure leaves {} in place (safe — defaults render).
  firmColourScheme: {},
  userColourScheme: {},
  caseHistoryVisibility: null,
  loadColourSchemes: async () => {
    try {
      // Also loads the firm's case-history-visibility setting here — same
      // member-readable, once-on-auth pattern, one effect. Kept in this
      // action (rather than a separate loader) so it rides the existing
      // isAuthenticated effect in App.tsx without new wiring.
      const [firm, user, historyVisibility] = await Promise.all([
        getFirmColourSchemeApi(),
        getUserColourSchemeApi(),
        getCaseHistoryVisibilityApi(),
      ])
      // getUserColourSchemeApi returns { currentScheme, savedTemplate };
      // only currentScheme drives rendering (top cascade layer).
      set({
        firmColourScheme: firm,
        userColourScheme: user.currentScheme,
        caseHistoryVisibility: historyVisibility,
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Store] Failed to load colour schemes:', err)
      // Leave {} — shipped defaults render, no visible breakage. Colour
      // failure leaves caseHistoryVisibility null → History stays hidden
      // for non-admins (fail-safe), admins are unaffected (canView ⊃ admin).
    }
  },
  setFirmColourScheme: (scheme: Record<string, string>) => set({ firmColourScheme: scheme }),
  setUserColourScheme: (scheme: Record<string, string>) => set({ userColourScheme: scheme }),
  setCaseHistoryVisibility: (value: CaseHistoryVisibility) => set({ caseHistoryVisibility: value }),
  // ── SHARED FIRM USERS ──────────────────────────────────
  firmUsers: [],
  firmUsersLoaded: false,
  firmUsersLoading: false,
  loadFirmUsers: async (force = false) => {
    const s = get()
    // Load-once: skip if already loaded (unless a forced refresh, e.g.
    // after a team change) or if a load is already in flight.
    if (s.firmUsersLoading) return
    if (s.firmUsersLoaded && !force) return
    set({ firmUsersLoading: true })
    try {
      const users = await listUsersApi()
      set({ firmUsers: users, firmUsersLoaded: true, firmUsersLoading: false })
    } catch (err) {
      // GET /api/users is open to every signed-in member (only its PATCH
      // routes are admin-gated), so a failure here is a real error. Leave
      // firmUsers as-is, don't set firmUsersLoaded (a retry can load), and
      // clear the in-flight flag. Name renders fall back to row snapshots.
      // eslint-disable-next-line no-console
      console.error('[Store] loadFirmUsers failed:', err)
      set({ firmUsersLoading: false })
    }
  },
  getFirmUserById: (id) => get().firmUsers.find((u) => u.id === id) ?? null,
  applyUserDisplayUpdate: (u) => {
    const s = get()
    const style = (u.avatarStyle === 'solid' || u.avatarStyle === 'soft' || u.avatarStyle === 'icon') ? u.avatarStyle : undefined
    // avatarPhotoUrl: key PRESENT ⇒ the photo changed (URL, or null = removed);
    // absent ⇒ leave the photo as-is.
    const photoChanged = 'avatarPhotoUrl' in u
    const firmUsers = s.firmUsers.map((f) => f.id === u.id
      ? { ...f, firstName: u.firstName, lastName: u.lastName, avatarColour: u.avatarColour ?? f.avatarColour, avatarStyle: style ?? f.avatarStyle, ...(photoChanged ? { avatarPhotoUrl: u.avatarPhotoUrl ?? null } : {}) }
      : f)
    const me = s.authUser
    if (me && me.id === u.id && (me.firstName !== u.firstName || me.lastName !== u.lastName || (u.avatarColour && me.avatarColour !== u.avatarColour) || (style && me.avatarStyle !== style) || (photoChanged && (me.avatarPhotoUrl ?? null) !== (u.avatarPhotoUrl ?? null)))) {
      // Our own identity changed elsewhere (another tab/device) — go through
      // setAuthUser so localStorage + presence display stay in step.
      set({ firmUsers })
      s.setAuthUser({ ...me, firstName: u.firstName, lastName: u.lastName, avatarColour: u.avatarColour ?? me.avatarColour, avatarStyle: style ?? me.avatarStyle, ...(photoChanged ? { avatarPhotoUrl: u.avatarPhotoUrl ?? null } : {}) })
      return
    }
    set({ firmUsers })
  },
  // ── FAVOURITES ─────────────────────────────────────────
  favourites: null,
  loadFavourites: async () => {
    try {
      const favourites = await listFavouritesApi()
      // (audit) A load that lands while a toggle is still syncing must not
      // undo it: re-apply every in-flight item's DESIRED state on top.
      set({ favourites: overlayPendingFavourites(favourites) })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Store] Failed to load favourites:', err)
      // Leave whatever's cached (null or prior list) — the star just
      // reads as "not favourited" for unknown items; no visible breakage.
    }
  },
  isFavourited: (itemType, itemId) => {
    const list = get().favourites
    return !!list && list.some((f) => f.itemType === itemType && f.itemId === itemId)
  },
  favouritesError: null,
  setFavouritesError: (message) => set({ favouritesError: message }),
  removeFavouriteById: async (id, toastMessage) => {
    // Optimistic drop from the cache so the dead row disappears at once.
    const list = get().favourites ?? []
    // (audit) If a toggle for this item is mid-sync, make it settle to
    // "removed" rather than re-inserting the dead row when it finishes.
    const dead = list.find((f) => f.id === id)
    const pending = dead ? favSync.get(`${dead.itemType}:${dead.itemId}`) : undefined
    if (pending) { pending.want = false; pending.server = null }
    set({ favourites: list.filter((f) => f.id !== id) })
    // Reuse the favourites toast channel for the informational message.
    if (toastMessage) set({ favouritesError: toastMessage })
    // Best-effort server delete — NOT reverted on failure. The target is
    // gone (that's why we're here), so the row should stay removed; in the
    // normal case the delete-handler cascade has already removed it and
    // this DELETE is a harmless 404.
    try {
      await removeFavouriteApi(id)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Store] Failed to remove dead favourite (non-fatal):', err)
    }
  },
  toggleFavourite: ({ itemType, itemId, label, subSection }) => {
    // ★ RACE-SAFE TOGGLE (desired-state + per-item serialised sync).
    // Rapid clicks used to fire overlapping POST/DELETEs: a click during an
    // in-flight add found the optimistic TEMP row and DELETEd `temp-…` → 404
    // "Favourite not found" → reverted to the list WITH the temp row → the
    // POST then landed → stuck favourited (+ a 404 per extra click).
    // Now:
    //   1. Every click flips the DESIRED state for this item and updates the
    //      star at once (optimistic), with no request of its own.
    //   2. ONE worker per item (itemType:itemId) reconciles server → desired,
    //      one request at a time, re-reading `desired` after each — so N
    //      clicks collapse to at most the requests needed, in order.
    //   3. Already-done is success: POST is an upsert (favouritesController
    //      addFavourite), and a DELETE 404 means it's already gone.
    //   4. A genuine failure (network, cap 409, 5xx) stops the worker, snaps
    //      the star to the server's real state and surfaces the error.
    if (get().favouritesError) set({ favouritesError: null })
    const key = `${itemType}:${itemId}`
    const list = get().favourites ?? []
    const rows = list.filter((f) => f.itemType === itemType && f.itemId === itemId)
    const want = rows.length === 0 // flip what the user SEES
    let st = favSync.get(key)
    if (!st) {
      // Seed the server truth from the cache (a temp row is never on the server).
      st = { want, label, subSection: subSection ?? null, server: rows.find((f) => !f.id.startsWith('temp-')) ?? null, running: null }
      favSync.set(key, st)
    }
    st.want = want
    st.label = label
    st.subSection = subSection ?? null
    // Optimistic star.
    if (want) {
      set({ favourites: [...list, {
        id: `temp-${itemType}-${itemId}-${Date.now()}`, itemType, itemId, subSection: subSection ?? null,
        label, sortOrder: list.length, createdAt: new Date().toISOString(),
      }] })
    } else {
      set({ favourites: list.filter((f) => !(f.itemType === itemType && f.itemId === itemId)) })
    }
    if (st.running) return st.running
    const s = st
    // Put THIS item's rows in the list to exactly match the server row.
    const settle = (): void => {
      if (!get().authUser) return // (audit) logged out mid-sync — don't write into the next session
      set((state) => {
        const cur = state.favourites ?? []
        const others = cur.filter((f) => !(f.itemType === itemType && f.itemId === itemId))
        if (!s.server) return { favourites: others }
        const idx = cur.findIndex((f) => f.itemType === itemType && f.itemId === itemId)
        if (idx === -1) return { favourites: [...others, s.server] }
        const next = [...others]
        next.splice(Math.min(idx, next.length), 0, s.server)
        return { favourites: next }
      })
    }
    s.running = (async () => {
      try {
        for (;;) {
          if (s.want && !s.server) {
            try {
              s.server = await addFavouriteApi({ itemType, itemId, label: s.label, subSection: s.subSection })
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('[Store] Failed to add favourite:', err)
              s.want = false // the server doesn't have it — show that
              set({ favouritesError: err instanceof Error ? err.message : 'Could not add to favourites' })
              break
            }
          } else if (!s.want && s.server) {
            try {
              await removeFavouriteApi(s.server.id)
              s.server = null
            } catch (err) {
              if ((err as { response?: { status?: number } })?.response?.status === 404) {
                s.server = null // already gone = desired state reached
              } else {
                // eslint-disable-next-line no-console
                console.error('[Store] Failed to remove favourite:', err)
                s.want = true // still on the server — re-fill the star
                set({ favouritesError: err instanceof Error ? err.message : 'Could not remove favourite' })
                break
              }
            }
          } else {
            break // server matches the latest click
          }
        }
      } finally {
        settle()
        favSync.delete(key)
      }
    })()
    return s.running
  },
  reorderFavourites: async (orderedIds) => {
    const prev = get().favourites
    if (!prev) return
    // Optimistic: reorder the cached list to match orderedIds immediately
    // so the sidebar reflects the drop before the round-trip. Any id not in
    // orderedIds (shouldn't happen) is appended at the end to avoid dropping
    // a row. sortOrder is left as-is optimistically; the server response
    // below reconciles the authoritative sortOrder values.
    const byId = new Map(prev.map((f) => [f.id, f]))
    const reordered: Favourite[] = []
    for (const id of orderedIds) {
      const f = byId.get(id)
      if (f) { reordered.push(f); byId.delete(id) }
    }
    // Leftovers keep original order. Array.from(...) avoids iterating the
    // Map iterator directly (TS target < es2015 downlevel-iteration guard).
    Array.from(byId.values()).forEach((f) => reordered.push(f))
    // No-op guard: if the order didn't actually change, skip the write.
    if (reordered.every((f, i) => f.id === prev[i]?.id)) return
    set({ favouritesError: null, favourites: reordered })
    try {
      const saved = await reorderFavouritesApi(orderedIds)
      set({ favourites: saved }) // reconcile with normalised sortOrder
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Store] Failed to reorder favourites:', err)
      set({
        favourites: prev, // revert to the pre-drag order
        favouritesError: err instanceof Error ? err.message : 'Could not reorder favourites',
      })
    }
  },
  recentCases: null,
  setRecentCases: (cases: RawCase[] | null) => set({ recentCases: cases }),
  renamedCaseNames: {},
  applyCaseRename: (caseId, caseName, favouriteLabel) => {
    set((state) => {
      const patch: Partial<CasevoStore> = {}
      // Open-case header consistency (if the renamed case is the one open).
      if (state.currentCase?.id === caseId) {
        patch.currentCase = { ...state.currentCase, caseName, updatedAt: new Date().toISOString() }
      }
      // Sidebar Recent — the RawCase rows carry caseName; the display is
      // hybrid, so patching caseName updates the label on next render.
      if (state.recentCases) {
        patch.recentCases = state.recentCases.map((c) => (c.id === caseId ? { ...c, caseName } : c))
      }
      // Rename registry — lets local-state surfaces (JumpBackInCard's
      // day-stepper) overlay the new name immediately (see the type doc).
      patch.renamedCaseNames = { ...state.renamedCaseNames, [caseId]: caseName }
      // Sidebar Favourites — the label is a stored denormalised copy; the caller
      // passes the rebuilt "<name> · <REF>" (caseNameWithRef — same as the star).
      const fav = (state.favourites ?? []).find((f) => f.itemType === 'case' && f.itemId === caseId)
      if (fav) {
        const newLabel = favouriteLabel
        patch.favourites = (state.favourites ?? []).map((f) => (f.id === fav.id ? { ...f, label: newLabel } : f))
      }
      return patch
    })
    // Persist the favourite's refreshed label so it survives a full reload.
    // addFavourite is an upsert that refreshes label + keeps the row's
    // position (see favouritesController). Fire-and-forget: on failure the
    // in-session label is already correct and only reverts on the next
    // favourites reload.
    const fav = (get().favourites ?? []).find((f) => f.itemType === 'case' && f.itemId === caseId)
    if (fav) {
      void addFavouriteApi({ itemType: 'case', itemId: caseId, label: fav.label, subSection: fav.subSection ?? null })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[Store] Failed to persist favourite label after rename (non-fatal):', err)
        })
    }
  },
  loadRecentCases: async () => {
    try {
      const cases = await fetchCases({ recent: true })
      set({ recentCases: cases })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Store] Failed to load recent cases:', err)
      set({ recentCases: [] })
    }
  },
  // ── CALENDAR ──────────────────────────────────────────
  calendarEntries: null,
  calendarError: null,
  setCalendarEntries: (entries: CalendarEntry[] | null) => set({ calendarEntries: entries }),
  loadCalendarEntries: async () => {
    // Clear any prior error so a retry starts clean (null entries → the
    // view shows its loading state, not a stale banner).
    set({ calendarError: null })
    try {
      const entries = await fetchCalendarApi()
      set({ calendarEntries: entries, calendarError: null })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Store] Failed to load calendar entries:', err)
      // Set an ERROR (not an empty list) so the view shows a retry banner
      // rather than a misleading "No deadlines yet" on a transient failure.
      set({ calendarEntries: null, calendarError: 'Unable to load your calendar. Please try again.' })
    }
  },
  // ── DOCUMENT SEARCH ────────────────────────────────────
  documentSearchResults: null,
  documentSearchError: null,
  setDocumentSearchResults: (results: DocumentSearchResult[] | null) =>
    set({ documentSearchResults: results }),
  runDocumentSearch: async (options) => {
    set({ documentSearchError: null })
    try {
      const results = await fetchDocumentSearchApi(options)
      set({ documentSearchResults: results, documentSearchError: null })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Store] Failed to search documents:', err)
      // Error (not empty) so the view shows a retry banner instead of a
      // misleading "No documents found" on a transient failure.
      set({ documentSearchResults: null, documentSearchError: 'Unable to search documents. Please try again.' })
    }
  },
  // ── ONBOARDING ─────────────────────────────────────────
  firmCaseCount: null,
  setFirmCaseCount: (count: number | null) => set({ firmCaseCount: count }),
  // ── AUTO-SAVE ERROR ────────────────────────────────────
  clientNavTick: 0,
  bumpClientNav: () => set((st) => ({ clientNavTick: st.clientNavTick + 1 })),
  saveError: null,
  setSaveError: (error: Error | null) => set({ saveError: error }),
  saveConflict: null,
  setSaveConflict: (message: string | null) => set({ saveConflict: message }),
  // ── CASES ───────────────────────────────────────────────
  currentCase: null,
  isCaseLoading: false,
  // Seeded from the STORED user's saved toggles (sidebar.collapsed,
  // casenav.*, section toggles…) so a refresh lays out right at once — no
  // jump while /auth/me loads. Bad / missing storage → the defaults.
  viewingPaneToggles: (() => {
    try {
      return seedToggles(startUpSession.user?.viewingPaneToggles)
    } catch { return buildDefaultToggles() }
  })(),
  // Read-only mirror of authUser.role — NOT user-settable (the self-serve
  // switcher was removed). Hydrated from the stored user on init so a page
  // refresh keeps a real admin's admin sections without a flash; setAuth /
  // setAuthUser re-derive it. Admin status client-side has exactly one
  // source: the server-provided role. (Server requireAdmin is the real guard.)
  userRole: startUpSession.user?.role === 'admin' ? 'admin' : 'user',
  setCurrentCaseById: async (id: string, preloadedRaw?: RawCase, onShown?: () => void) => {
    // ★ (case-page audit) Latest-open-wins: a slow fetch for case A must not
    // land on top of case B the user opened after it.
    // A REFRESH of the case already open (link change, client relink, Back
    // restore) must not supersede a different case the user opens meanwhile,
    // so it doesn't bump the counter; it instead lands only if that case is
    // still the open one.
    const isRefresh = get().currentCase?.id === id
    const openToken = isRefresh ? caseOpenSeq : ++caseOpenSeq
    const superseded = (): boolean => openToken !== caseOpenSeq || (isRefresh && get().currentCase?.id !== id)
    // Nav loading flag — true for the whole fetch, cleared in the finally
    // (even on throw). ViewingPane shows a spinner INSTEAD of the
    // CaseListScreen fallback while this is true AND currentCase is null,
    // so Back (restoreView nulls currentCase up-front) and cold-open don't
    // flash the case list. Null-gated in ViewingPane, so a refresh of an
    // already-open case (currentCase stays non-null here) never flashes a
    // spinner over the detail.
    set({ isCaseLoading: true })
    try {
    // Use the caller's already-fetched row when provided (quick-view modal) so
    // the case shell paints WITHOUT a second getCaseById round-trip; otherwise
    // fetch it. The satellite fetches below run either way.
    let d: RawCase
    try {
      // Shared fetch: joins the quick-view's in-flight getCaseById for this
      // case (if any) instead of firing a second cold request.
      d = preloadedRaw ?? await fetchCaseByIdShared(id)
    } catch (err) {
      // ★ Blocked / missing case (403 / 404 from the case-access guard): every
      // open path funnels through here (list, sidebar, calendar, dashboard,
      // notifications, deep links), so handle it ONCE — no broken half-open
      // page: clear the case (the view falls back to the case list) and show a
      // clean message in the shared toast. Other errors still propagate.
      const msg = caseOpenErrorMessage(err)
      if (!msg) {
        // ★ (Audit) A network / 5xx failure used to be rethrown to callers that
        // only console.error — the spinner cleared and NOTHING happened. Tell
        // the user (unless a newer open superseded this one), then rethrow.
        if (!superseded()) get().setSaveConflict('Couldn’t open that case — check your connection and try again.')
        throw err
      }
      if (superseded()) return
      set({ currentCase: null })
      get().setSaveConflict(msg)
      return
    }
    // Superseded by a later open → drop this result (no currentCase swap).
    if (superseded()) return
    // Capture the SERVER updatedAt from the RAW fetch response, before the
    // mapping below and before any local edit stamps currentCase.updatedAt.
    // This is the optimistic-concurrency baseline the autosave sends back.
    setCaseBaseUpdatedAt(d.id, d.updatedAt)
    {
      const mappedCase: Case = {
        id: d.id,
        caseControl: {
          handover: d.handover,
          openDate: d.openDate,
          closeDate: d.closeDate,
          setupFinalised: d.setupFinalised,
        },
        // Phase 4: backend's getCaseById embeds the linked Client.
        // Defensive mapping mirrors the case-side pattern — every
        // string column has an empty-string fallback in case a
        // pre-migration row comes back with nulls. Tri-state fields
        // (title, dateOfBirth, hasNiNumber, employmentStatus) keep
        // `null` distinct from `''` because the section's UI logic
        // depends on the distinction.
        client: d.client ? {
          id: d.client.id,
          firmId: d.client.firmId,
          clientNumber: d.client.clientNumber,
          title: d.client.title ?? null,
          forename: d.client.forename || '',
          surname: d.client.surname || '',
          addressLine1: d.client.addressLine1 || '',
          addressLine2: d.client.addressLine2 || '',
          addressTown: d.client.addressTown || '',
          addressCounty: d.client.addressCounty || '',
          addressPostcode: d.client.addressPostcode || '',
          dateOfBirth: d.client.dateOfBirth ?? null,
          hasNiNumber: d.client.hasNiNumber ?? null,
          niNumber: d.client.niNumber || '',
          mainContactNumber: d.client.mainContactNumber || '',
          emailAddress: d.client.emailAddress || '',
          hasEmailAddress: d.client.hasEmailAddress ?? null,
          employmentStatus: d.client.employmentStatus ?? null,
          occupation: d.client.occupation || '',
          // Phase 4.5 GP/Hospital fields. Declared optional on the
          // Client type (`field?: string`), so we use `?? ''` rather
          // than `|| ''` to make the undefined→'' coercion explicit.
          // Pre-Phase-4.5 rows come back without these keys at all;
          // post-migration rows have them as empty strings via the
          // backend's @default('').
          gpName: d.client.gpName ?? '',
          gpAddressLine1: d.client.gpAddressLine1 ?? '',
          gpAddressLine2: d.client.gpAddressLine2 ?? '',
          gpAddressTown: d.client.gpAddressTown ?? '',
          gpAddressCounty: d.client.gpAddressCounty ?? '',
          gpAddressPostcode: d.client.gpAddressPostcode ?? '',
          gpPhone: d.client.gpPhone ?? '',
          hospitalName: d.client.hospitalName ?? '',
          hospitalAddressLine1: d.client.hospitalAddressLine1 ?? '',
          hospitalAddressLine2: d.client.hospitalAddressLine2 ?? '',
          hospitalAddressTown: d.client.hospitalAddressTown ?? '',
          hospitalAddressCounty: d.client.hospitalAddressCounty ?? '',
          hospitalAddressPostcode: d.client.hospitalAddressPostcode ?? '',
          hospitalPhone: d.client.hospitalPhone ?? '',
          createdAt: d.client.createdAt,
          updatedAt: d.client.updatedAt,
        } : null,
        clientSource: {
          name: d.clientSource || '',
          contactNumber: d.clientSourceContactNumber || '',
          emailAddress: d.clientSourceEmailAddress || '',
          addressLine1: d.clientSourceAddressLine1 || '',
          addressLine2: d.clientSourceAddressLine2 || '',
          addressTown: d.clientSourceAddressTown || '',
          addressCounty: d.clientSourceAddressCounty || '',
          addressPostcode: d.clientSourceAddressPostcode || '',
          notApplicable: d.clientSourceNotApplicable || false,
        },
        clientDetails: {
          title: d.clientTitle || '',
          forename: d.clientForename || '',
          surname: d.clientSurname || '',
          // Stage 2b: flat 5-field address mirroring the Case row's flat
          // backend columns. The case-page editor binds inputs directly
          // to these keys (no nested Address object to compose/decompose).
          addressLine1: d.clientAddressLine1 || '',
          addressLine2: d.clientAddressLine2 || '',
          addressTown: d.clientAddressTown || '',
          addressCounty: d.clientAddressCounty || '',
          addressPostcode: d.clientAddressPostcode || '',
          dateOfBirth: d.clientDateOfBirth,
          niNumber: d.clientNiNumber || '',
          // Backend column is nullable Boolean. `?? null` keeps the
          // tri-state — undefined/null from the API both mean "not yet
          // answered" so neither radio renders as checked.
          hasNiNumber: d.clientHasNiNumber ?? null,
          mainContactNumber: d.clientMainContactNumber || '',
          emailAddress: d.clientEmailAddress || '',
          // B3 — "Client has no email address" (tri-state, like hasNiNumber).
          hasEmailAddress: d.clientHasEmailAddress ?? null,
          employmentStatus: d.clientEmploymentStatus || null,
          occupation: d.clientOccupation || '',
          timeOffWork: d.clientTimeOffWork,
          daysOffWork: d.clientDaysOffWork,
          descriptionOfInjuries: d.clientDescriptionOfInjuries || '',
          medicalAttention: d.clientMedicalAttention || [],
          // GP and Hospital snapshot fields. Wired up now that RawCase
          // carries Reuben's clientGp* / clientHospital* columns (Client
          // Snapshot Model, Stage 1). The live Client values continue to
          // feed mappedCase.client.* above (L514+); these snapshot values
          // are populated but UNUSED FOR RENDERING in this stage — the
          // ClientDetailsSection still reads from currentCase.client.*
          // until the snapshot-read switch lands in a later stage.
          // `?? ''` matches the Client mapping style at L514 — explicit
          // undefined→'' coercion against any historical row that came
          // back without these keys; backend @default('') means we'll
          // usually see '' rather than undefined at runtime anyway.
          gpName: d.clientGpName ?? '',
          gpAddressLine1: d.clientGpAddressLine1 ?? '',
          gpAddressLine2: d.clientGpAddressLine2 ?? '',
          gpAddressTown: d.clientGpAddressTown ?? '',
          gpAddressCounty: d.clientGpAddressCounty ?? '',
          gpAddressPostcode: d.clientGpAddressPostcode ?? '',
          gpPhone: d.clientGpPhone ?? '',
          hospitalName: d.clientHospitalName ?? '',
          hospitalAddressLine1: d.clientHospitalAddressLine1 ?? '',
          hospitalAddressLine2: d.clientHospitalAddressLine2 ?? '',
          hospitalAddressTown: d.clientHospitalAddressTown ?? '',
          hospitalAddressCounty: d.clientHospitalAddressCounty ?? '',
          hospitalAddressPostcode: d.clientHospitalAddressPostcode ?? '',
          hospitalPhone: d.clientHospitalPhone ?? '',
        },
        accidentDetails: {
          askCueReference: d.askCueReference || '',
          accidentDate: d.accidentDate,
          accidentTime: d.accidentTime,
          vehiclePosition: d.vehiclePosition,
          driverName: d.driverName || '',
          accidentDescription: d.accidentDescription,
          accidentDescriptionOther: d.accidentDescriptionOther || '',
          accidentLocation: d.accidentLocation || '',
          accidentLocationMode: d.accidentLocationMode || null,
          accidentLocationFreeText: d.accidentLocationFreeText || '',
          accidentLocationAddressLine1: d.accidentLocationAddressLine1 || '',
          accidentLocationAddressLine2: d.accidentLocationAddressLine2 || '',
          accidentLocationAddressTown: d.accidentLocationAddressTown || '',
          accidentLocationAddressCounty: d.accidentLocationAddressCounty || '',
          accidentLocationAddressPostcode: d.accidentLocationAddressPostcode || '',
          wearingSeatbelt: d.wearingSeatbelt,
          claimantReg: d.claimantReg || '',
          claimantVehicleResponsible: d.claimantVehicleResponsible,
          numberOfPeopleInVehicle: d.numberOfPeopleInVehicle,
          policeInvolvement: d.policeInvolvement,
        },
        injuryDetails: {
          neck: d.injuryNeck || false,
          shoulder: d.injuryShoulder || false,
          back: d.injuryBack || false,
          ribsChestTorso: d.injuryRibsChestTorso || false,
          faceCheckboneJawNose: d.injuryFaceCheckboneJawNose || false,
          forearmWristHandFingers: d.injuryForearmWristHandFingers || false,
          damageToTeeth: d.injuryDamageToTeeth || false,
          headSenses: d.injuryHeadSenses || false,
          hipsPelvisGenitals: d.injuryHipsPelvisGenitals || false,
          legKneeAnkleFootToe: d.injuryLegKneeAnkleFootToe || false,
          psychological: d.injuryPsychological || false,
          // B3 (B2c item 4)
          hearingEars: d.injuryHearingEars || false,
          lungsBreathing: d.injuryLungsBreathing || false,
          skin: d.injurySkin || false,
          internalOrgans: d.injuryInternalOrgans || false,
          fatal: d.injuryFatal || false,
          other: d.injuryOther || false,
          otherDetails: d.injuryOtherDetails || '',
          exceptionallySevere: d.injuryExceptionallySevere || false,
          exceptionallySevereDetails: d.injuryExceptionallySevereDetails || '',
        },
        defendant: {
          reg: d.defendantReg || '',
          name: d.defendantName || '',
          // Build the address when ANY part is set (not just line 1) — a saved
          // town/postcode with a blank line 1 must still show after reload.
          address: hasAddressPart(d.defendantAddressLine1, d.defendantAddressLine2, d.defendantAddressTown, d.defendantAddressCounty, d.defendantAddressPostcode) ? {
            line1: d.defendantAddressLine1 || '',
            line2: d.defendantAddressLine2 || '',
            town: d.defendantAddressTown || '',
            county: d.defendantAddressCounty || '',
            postcode: d.defendantAddressPostcode || '',
          } : null,
          mainContactNumber: d.defendantMainContactNumber || '',
        },
        tpi: {
          insurer: d.tpiInsurer || '',
          // Backend column `tpiReferenceNumber` is String @default(""),
          // but `?? ''` keeps the frontend safe for rows that pre-date
          // the column being added.
          referenceNumber: d.tpiReferenceNumber ?? '',
          // Build the address when ANY part is set (not just line 1) — a saved
          // town/postcode with a blank line 1 must still show after reload.
          address: hasAddressPart(d.tpiAddressLine1, d.tpiAddressLine2, d.tpiAddressTown, d.tpiAddressCounty, d.tpiAddressPostcode) ? {
            line1: d.tpiAddressLine1 || '',
            line2: d.tpiAddressLine2 || '',
            town: d.tpiAddressTown || '',
            county: d.tpiAddressCounty || '',
            postcode: d.tpiAddressPostcode || '',
          } : null,
        },
        linkedCases: d.linkedCases || [],
        medicalAgencyName: d.medicalAgencyName || '',
        medicalAgencyAddressLine1: d.medicalAgencyAddressLine1 || '',
        medicalAgencyAddressLine2: d.medicalAgencyAddressLine2 || '',
        medicalAgencyAddressTown: d.medicalAgencyAddressTown || '',
        medicalAgencyAddressCounty: d.medicalAgencyAddressCounty || '',
        medicalAgencyAddressPostcode: d.medicalAgencyAddressPostcode || '',
        medicalAgencyPhone: d.medicalAgencyPhone || '',
        medicalAgencyEmail: d.medicalAgencyEmail || '',
        medicalAgencyNotes: d.medicalAgencyNotes || '',
        medicalExpertName: d.medicalExpertName || '',
        medicalExpertAddressLine1: d.medicalExpertAddressLine1 || '',
        medicalExpertAddressLine2: d.medicalExpertAddressLine2 || '',
        medicalExpertAddressTown: d.medicalExpertAddressTown || '',
        medicalExpertAddressCounty: d.medicalExpertAddressCounty || '',
        medicalExpertAddressPostcode: d.medicalExpertAddressPostcode || '',
        medicalExpertPhone: d.medicalExpertPhone || '',
        medicalExpertEmail: d.medicalExpertEmail || '',
        medicalExpertNotes: d.medicalExpertNotes || '',
        tpsName: d.tpsName || '',
        tpsAddressLine1: d.tpsAddressLine1 || '',
        tpsAddressLine2: d.tpsAddressLine2 || '',
        tpsAddressTown: d.tpsAddressTown || '',
        tpsAddressCounty: d.tpsAddressCounty || '',
        tpsAddressPostcode: d.tpsAddressPostcode || '',
        tpsPhone: d.tpsPhone || '',
        tpsEmail: d.tpsEmail || '',
        tpsNotes: d.tpsNotes || '',
        // Funding / OIC fields — String @default("") on the backend,
        // `|| ''` guards against any historical row that came back
        // as undefined.
        deductionMode: d.deductionMode || '',
        deductionCustomValue: d.deductionCustomValue || '',
        ateProvider: d.ateProvider || '',
        // Stage A: assignee passthrough. The scalar id is passed as-is
        // (nullable both sides); the nested `assignedTo` join is
        // forwarded when the backend attached it (getCaseById includes
        // it). No `|| ''` guard here — this field's null state is
        // meaningful ("Unassigned"), not a defensive default.
        assignedToUserId: d.assignedToUserId,
        assignedTo: d.assignedTo,
        // Stage D: matterNumber passthrough. Optional both sides —
        // pre-Stage-D data doesn't have it selected, so leaving it
        // undefined is valid. Backend always sends it going forward.
        matterNumber: d.matterNumber,
        // Human-readable case title (nullable). Passthrough — header
        // shows `caseName || client name`. Set via the list's Rename action.
        caseName: d.caseName ?? null,
        // Real firm-wide case links from getCaseById's caseLinks key
        // (empty array when the case has none). Distinct from the dead
        // linkedCases scalar mapped elsewhere.
        caseLinks: d.caseLinks ?? [],
        // Shared section-completion (per-case). Seeds useSectionCompletion
        // on open so every user sees the same completed sections.
        completedSections: d.completedSections ?? [],
        // Claim group (B2) + who set the case's own limitation date.
        claim: claimOfRaw(d),
        limitationOverrideSetBy: d.limitationOverrideSetBy ?? null,
        // (B2b) who confirmed "no time limit" (child sexual abuse), while confirmed.
        abuseNoLimitConfirmedBy: d.abuseNoLimitConfirmedBy ?? null,
        protectedParty: d.piMatter?.protectedParty === true,
        // Audience (multi-assignee batch) — hydrated so any currentCase reader
        // sees the real handling team / firm-wide flag (was dropped here).
        firmWide: d.firmWide ?? false,
        assignees: d.assignees ?? [],
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
      }
      set({ currentCase: mappedCase })
      // The case can paint now (shell + hero + Overview with loading tiles).
      try { onShown?.() } catch { /* caller's view switch — never block the load */ }
      // ★ The four satellite loads (witnesses, deadlines, TPI contacts,
      // files) run CONCURRENTLY. They were awaited one after another (despite
      // the "in parallel" comments), so the case page's counts arrived ~4
      // round-trips late. Each keeps its own try/catch + identical state
      // writes; isCaseLoading still clears only once all have settled.
      await Promise.all([
        // Fetch witnesses for this case in parallel with the case data
        // mapping above (the await on fetchCaseByIdShared has already settled).
        // Failures here shouldn't block the case from opening — log and
        // leave the witnesses array empty so the user can still add
        // some. The catch keeps the promise rejection from surfacing as
        // an unhandled rejection in the console.
        (async () => {
          try {
            const witnesses = await listWitnessesApi(d.id)
            // Capture each witness's SERVER updatedAt + column VALUES (loaded state)
            // for the optimistic-concurrency guard + the column-aware 409 recovery.
            witnesses.forEach((w) => {
              setWitnessBaseUpdatedAt(w.id, w.updatedAt)
              setWitnessBaseValues(w.id, w as unknown as Record<string, unknown>)
            })
            set((state) => ({
              witnesses: { ...state.witnesses, [d.id]: witnesses },
            }))
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[Store] Failed to load witnesses:', err)
            set((state) => ({
              witnesses: { ...state.witnesses, [d.id]: [] },
            }))
          }
        })(),
        // Fetch case deadlines — same shape as the witnesses fetch
        // above. Failure leaves an empty array so the user can still
        // add deadlines without the case open being blocked.
        (async () => {
          try {
            const deadlines = await listCaseDeadlinesShared(d.id)
            // Capture each deadline's SERVER updatedAt + column VALUES (loaded state)
            // for the optimistic-concurrency guard + the column-aware 409 recovery.
            deadlines.forEach((dl) => {
              setDeadlineBaseUpdatedAt(dl.id, dl.updatedAt)
              setDeadlineBaseValues(dl.id, dl as unknown as Record<string, unknown>)
            })
            set((state) => ({
              caseDeadlines: { ...state.caseDeadlines, [d.id]: deadlines },
            }))
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[Store] Failed to load case deadlines:', err)
            // ★ (Limitation audit, HIGH) Do NOT store [] on failure: an empty list
            // reads as "no Limitation date deadline", so the hero / Overview / PDF
            // showed accident + 3y as the limitation (possibly LATER than the real
            // saved date) and the Deadlines section offered a duplicate one-click
            // add. Leave the slot unloaded (surfaces wait / show loading) and tell
            // the user to reload.
            if (get().currentCase?.id === d.id) {
              get().setSaveConflict('Couldn’t load this case’s deadlines, so its limitation date isn’t shown. Reopen the case to try again.')
            }
          }
        })(),
        // Refresh the firm's TPI contact directory too. It's firm-wide
        // (not case-specific) but loading it here ensures any contact
        // a colleague added since login is visible without a full
        // page reload.
        (async () => {
          try {
            const contacts = await listTpiContactsApi()
            set({ tpiContacts: contacts })
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[Store] Failed to load TPI contacts:', err)
            // Leave the existing list (possibly stale, but better than
            // wiping a successful prior load). If this was the first
            // load, the array is the default empty.
          }
        })(),
        // Fetch this case's file attachments in parallel too. Same
        // non-fatal pattern as witnesses — log on failure, leave the
        // slice empty so the Documents section shows "No documents
        // yet" (or the user can retry).
        (async () => {
          try {
            const files = await listFilesApi(d.id)
            set((state) => ({
              files: { ...state.files, [d.id]: files },
            }))
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[Store] Failed to load files:', err)
            set((state) => ({
              files: { ...state.files, [d.id]: [] },
            }))
          }
        })(),
      ])
    }
    } finally {
      if (openToken === caseOpenSeq) set({ isCaseLoading: false })
    }
  },
  updateCase: (updates) =>
    set((state) => ({
      currentCase: state.currentCase
        ? { ...state.currentCase, ...updates, updatedAt: new Date().toISOString() }
        : null,
    })),
  clearCurrentCase: () => set({ currentCase: null }),
  toggleHeading: (heading) => {
    set((state) => ({
      viewingPaneToggles: {
        ...state.viewingPaneToggles,
        [heading]: !state.viewingPaneToggles[heading],
      },
    }))
    scheduleTogglesSave(get().viewingPaneToggles)
  },
  setToggles: (toggles) => {
    set({ viewingPaneToggles: toggles })
    scheduleTogglesSave(toggles)
  },
  setToggle: (key, value) => {
    // Merge one key into the current map + schedule a debounced
    // save. `get()` reads the fresh post-set state so the payload
    // matches what the UI just optimistically rendered.
    set((state) => ({
      viewingPaneToggles: { ...state.viewingPaneToggles, [key]: value },
    }))
    scheduleTogglesSave(get().viewingPaneToggles)
  },
  finaliseSetup: () =>
    set((state) => ({
      currentCase: state.currentCase
        ? {
            ...state.currentCase,
            caseControl: { ...state.currentCase.caseControl, setupFinalised: true },
            updatedAt: new Date().toISOString(),
          }
        : null,
    })),
  // ── WITNESSES ─────────────────────────────────────────────
  // Optimistic-update setters. The component is responsible for
  // calling the API after these — the store just tracks UI state.
  witnesses: {},
  setWitnesses: (caseId, list) =>
    set((state) => ({ witnesses: { ...state.witnesses, [caseId]: list } })),
  addWitness: (caseId, witness) =>
    set((state) => ({
      witnesses: {
        ...state.witnesses,
        [caseId]: [...(state.witnesses[caseId] ?? []), witness],
      },
    })),
  updateWitness: (witness) =>
    set((state) => {
      const list = state.witnesses[witness.caseId]
      if (!list) return state
      return {
        witnesses: {
          ...state.witnesses,
          [witness.caseId]: list.map((w) => (w.id === witness.id ? witness : w)),
        },
      }
    }),
  removeWitness: (caseId, witnessId) =>
    set((state) => ({
      witnesses: {
        ...state.witnesses,
        [caseId]: (state.witnesses[caseId] ?? []).filter((w) => w.id !== witnessId),
      },
    })),
  // ── CASE DEADLINES ─────────────────────────────────────────
  // Exact mirror of the witnesses slice — per-case keyed map.
  // updateCaseDeadline reads caseId off the row itself (same as
  // updateWitness) so the section component doesn't need to pass
  // the caseId separately.
  caseDeadlines: {},
  setCaseDeadlines: (caseId, list) =>
    set((state) => ({ caseDeadlines: { ...state.caseDeadlines, [caseId]: list } })),
  addCaseDeadline: (caseId, deadline) =>
    set((state) => ({
      caseDeadlines: {
        ...state.caseDeadlines,
        [caseId]: [...(state.caseDeadlines[caseId] ?? []), deadline],
      },
    })),
  updateCaseDeadline: (deadline) =>
    set((state) => {
      const list = state.caseDeadlines[deadline.caseId]
      if (!list) return state
      return {
        caseDeadlines: {
          ...state.caseDeadlines,
          [deadline.caseId]: list.map((d) => (d.id === deadline.id ? deadline : d)),
        },
      }
    }),
  removeCaseDeadline: (caseId, deadlineId) =>
    set((state) => ({
      caseDeadlines: {
        ...state.caseDeadlines,
        [caseId]: (state.caseDeadlines[caseId] ?? []).filter((d) => d.id !== deadlineId),
      },
    })),
  // ── FILES ──────────────────────────────────────────────────
  files: {},
  setFiles: (caseId, list) =>
    set((state) => ({ files: { ...state.files, [caseId]: list } })),
  addFile: (caseId, file) =>
    set((state) => ({
      files: {
        ...state.files,
        [caseId]: [...(state.files[caseId] ?? []), file],
      },
    })),
  removeFile: (caseId, fileId) =>
    set((state) => ({
      files: {
        ...state.files,
        [caseId]: (state.files[caseId] ?? []).filter((f) => f.id !== fileId),
      },
    })),
  // ── TPI CONTACTS ──────────────────────────────────────────
  tpiContacts: [],
  setTpiContacts: (list) => set({ tpiContacts: list }),
  addTpiContact: (contact) =>
    set((state) => ({ tpiContacts: [...state.tpiContacts, contact] })),
  replaceTpiContact: (contact) =>
    set((state) => ({
      tpiContacts: state.tpiContacts.map((c) => (c.id === contact.id ? contact : c)),
    })),
  // ── CLIENTS ─────────────────────────────────────────────
  clients: [],
  clientsLoading: false,
  clientsError: null,
  loadClients: async (search?: string, sort?: string) => {
    set({ clientsLoading: true, clientsError: null })
    try {
      const clients = await listClientsApi(search, sort)
      set({ clients, clientsLoading: false })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load clients'
      console.error('[Store] Failed to load clients:', err)
      set({ clientsError: message, clientsLoading: false })
    }
  },
  createClient: async (payload: CreateClientPayload) => {
    const client = await createClientApi(payload)
    set((state) => ({ clients: [...state.clients, client] }))
    return client
  },
  updateClient: async (id: string, payload: UpdateClientPayload & { baseUpdatedAt?: string }) => {
    const updated = await updateClientApi(id, payload)
    set((state) => ({
      clients: state.clients.map((c) => (c.id === id ? updated : c)),
    }))
    return updated
  },
  deleteClient: async (id: string) => {
    await deleteClientApi(id)
    set((state) => ({
      clients: state.clients.filter((c) => c.id !== id),
    }))
    // Success-only (deleteClientApi throws on the P2003 restrict→409 or
    // any failure, skipping this): the backend cascade has removed any
    // favourite pinning this client, so re-fetch favourites in the
    // background to drop the dead row from the sidebar at once.
    // Fire-and-forget — never blocks the delete UX.
    void get().loadFavourites()
  },
}))
